import { useEffect, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import api from "../../services/api";

const C = {
  saffron:"#E8922A", saffronL:"#FDF0D5", saffronD:"#B86A10",
  sage:"#4A7C59",    sageL:"#EAF4EE",    sageD:"#2D5A3D",
  terra:"#B85C38",
  cream:"#FDFAF3",   creamD:"#F5EDD8",
  turmeric:"#F7C948",
  ink:"#2C1810",     inkMid:"#5C3D2E",   inkLight:"#9A7B60",
};

const MiniLotus = ({ size=20, color=C.saffron }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" style={{ flexShrink:0, display:"inline-block" }}>
    {[0,30,60,-30,-60,90].map((r,i)=>(
      <ellipse key={i} cx="40" cy="50" rx="7" ry="16"
        fill={color} opacity={i>3?0.45:i>1?0.7:0.95}
        transform={`rotate(${r} 40 50)`}/>
    ))}
    <circle cx="40" cy="44" r="7" fill={C.turmeric}/>
  </svg>
);

/* ═══════════════════════════════════════════════════════════
   PAGES CONFIG — every single text on the site is a field
═══════════════════════════════════════════════════════════ */
const PAGES = {
  home: {
    label:"🏠 Home",
    groups:[
      { id:"hero", title:"Hero Section",
        hint:"The very first thing visitors see — every word editable.",
        fields:[
          { key:"heroSanskrit",  label:"Sanskrit Line",        type:"text",     hint:'The small italic line at top, e.g. "ॐ सर्वे भवन्तु सुखिनः · May all beings be well"' },
          { key:"heroTitle",     label:"Hero Headline",        type:"textarea", hint:"Use \\n to break into two lines" },
          { key:"heroSubtitle",  label:"Hero Subtitle",        type:"textarea" },
          { key:"ctaText",       label:"Primary CTA Button",   type:"text",     hint:'e.g. "Book Appointment"' },
          { key:"ctaSecondary",  label:"Secondary CTA Button", type:"text",     hint:'e.g. "View Treatments"' },
          { key:"badge1",        label:"Trust Badge 1",        type:"text",     hint:'e.g. "⭐ 4.9 / 5 Rating"' },
          { key:"badge2",        label:"Trust Badge 2",        type:"text",     hint:'e.g. "🏥 Est. 2012"' },
          { key:"badge3",        label:"Trust Badge 3",        type:"text",     hint:'e.g. "🌿 100% Natural"' },
          { key:"badge4",        label:"Trust Badge 4",        type:"text",     hint:'e.g. "🔬 Evidence-Based"' },
          { key:"established",   label:"Established Year",     type:"text",     hint:"e.g. 2012" },
        ],
      },
      { id:"about", title:"About Strip",
        hint:"The two-column section with photo and about text.",
        fields:[
          { key:"aboutSectionTag",   label:"Section Tag (small caps)",     type:"text",     hint:'e.g. "About Our Clinic"' },
          { key:"aboutHeading1",     label:"Heading Line 1",               type:"text",     hint:'e.g. "Healing Rooted in"' },
          { key:"aboutHeading2",     label:"Heading Line 2 (coloured)",    type:"text",     hint:'e.g. "Classical Wisdom"' },
          { key:"aboutSummary",      label:"About Paragraph",              type:"textarea" },
          { key:"aboutBullet1",      label:"Bullet Point 1",               type:"text",     hint:'e.g. "Classical nadi pariksha and pulse diagnosis"' },
          { key:"aboutBullet2",      label:"Bullet Point 2",               type:"text",     hint:'e.g. "Authentic Ashtanga Hridayam protocols"' },
          { key:"aboutBullet3",      label:"Bullet Point 3",               type:"text",     hint:'e.g. "Zero synthetic additives in all formulations"' },
          { key:"aboutCta",          label:"About CTA Button",             type:"text",     hint:'e.g. "Learn More →"' },
          { key:"aboutEstLabel",     label:"Floating Badge Label",         type:"text",     hint:'e.g. "Established"' },
        ],
      },
      { id:"stats", title:"Stats Counter Bar",
        hint:"The green animated numbers band — numbers AND labels editable.",
        fields:[
          { key:"statPatients",      label:"Stat 1 — Number",  type:"text", hint:"e.g. 1200" },
          { key:"statPatientsLabel", label:"Stat 1 — Label",   type:"text", hint:'e.g. "Happy Patients"' },
          { key:"statYears",         label:"Stat 2 — Number",  type:"text", hint:"e.g. 12" },
          { key:"statYearsLabel",    label:"Stat 2 — Label",   type:"text", hint:'e.g. "Years of Practice"' },
          { key:"statTreatments",    label:"Stat 3 — Number",  type:"text", hint:"e.g. 25" },
          { key:"statTreatmentsLabel",label:"Stat 3 — Label",  type:"text", hint:'e.g. "Treatments Offered"' },
          { key:"statDoctors",       label:"Stat 4 — Number",  type:"text", hint:"e.g. 4" },
          { key:"statDoctorsLabel",  label:"Stat 4 — Label",   type:"text", hint:'e.g. "Expert Doctors"' },
        ],
      },
      { id:"treatments", title:"Treatments Section",
        hint:"Section tag, heading, subtitle, and all six treatment cards.",
        fields:[
          { key:"treatmentsTag",     label:"Section Tag",      type:"text",     hint:'e.g. "Our Specialities"' },
          { key:"treatmentsHeading", label:"Section Heading",  type:"text",     hint:'e.g. "Treatments We Offer"' },
          { key:"treatmentsSub",     label:"Section Subtitle", type:"textarea", hint:'e.g. "Time-tested Ayurvedic therapies..."' },
          { key:"treatmentsCta",     label:"See All Button",   type:"text",     hint:'e.g. "See All Treatments →"' },
          { key:"t1title", label:"Treatment 1 — Title",       type:"text"     },
          { key:"t1desc",  label:"Treatment 1 — Description", type:"textarea" },
          { key:"t2title", label:"Treatment 2 — Title",       type:"text"     },
          { key:"t2desc",  label:"Treatment 2 — Description", type:"textarea" },
          { key:"t3title", label:"Treatment 3 — Title",       type:"text"     },
          { key:"t3desc",  label:"Treatment 3 — Description", type:"textarea" },
          { key:"t4title", label:"Treatment 4 — Title",       type:"text"     },
          { key:"t4desc",  label:"Treatment 4 — Description", type:"textarea" },
          { key:"t5title", label:"Treatment 5 — Title",       type:"text"     },
          { key:"t5desc",  label:"Treatment 5 — Description", type:"textarea" },
          { key:"t6title", label:"Treatment 6 — Title",       type:"text"     },
          { key:"t6desc",  label:"Treatment 6 — Description", type:"textarea" },
        ],
      },
      { id:"trust", title:"Trust Band & Contact",
        hint:"The saffron-coloured band — every word editable.",
        fields:[
          { key:"trustSectionTag",   label:"Section Tag",       type:"text",     hint:'e.g. "Our Promise"' },
          { key:"trustText",         label:"Trust Statement",   type:"textarea" },
          { key:"trustCtaLabel",     label:"Call Button Label", type:"text",     hint:'e.g. "Call Us:"' },
          { key:"phone",             label:"Clinic Phone",      type:"text",     hint:"e.g. 9876543210" },
          { key:"whatsapp",          label:"WhatsApp Number",   type:"text",     hint:"Digits only, no spaces" },
        ],
      },
      { id:"online-consult", title:"Online Consultation Card",
        hint:"The dark navy card on the home page — headline, description, feature bullets, and side cards.",
        fields:[
          { key:"ocSectionTag",   label:"Section Tag (small badge)",  type:"text",     hint:'e.g. "Consult From Home"' },
          { key:"ocSectionTitle", label:"Section Heading",            type:"text",     hint:'e.g. "Online Consultation"' },
          { key:"ocSectionSub",   label:"Section Sub-heading",        type:"textarea", hint:'e.g. "Book a video or phone consultation..."' },
          { key:"ocBadgeLabel",   label:"Badge Label (top of card)",  type:"text",     hint:'e.g. "Available Worldwide"' },
          { key:"ocHeading1",     label:"Card Headline Line 1",       type:"text",     hint:'e.g. "Ayurvedic Care,"' },
          { key:"ocHeading2",     label:"Card Headline Line 2 (gold)",type:"text",     hint:'e.g. "Wherever You Are"' },
          { key:"ocDescription",  label:"Card Description Paragraph", type:"textarea", hint:"The paragraph below the headline" },
          { key:"ocFeat1Icon",    label:"Feature 1 — Icon (emoji)",   type:"text",     hint:'e.g. 📹' },
          { key:"ocFeat1Text",    label:"Feature 1 — Text",           type:"text",     hint:'e.g. "HD Video or Phone consultation"' },
          { key:"ocFeat2Icon",    label:"Feature 2 — Icon (emoji)",   type:"text",     hint:'e.g. 🌍' },
          { key:"ocFeat2Text",    label:"Feature 2 — Text",           type:"text",     hint:'e.g. "Works with any timezone worldwide"' },
          { key:"ocFeat3Icon",    label:"Feature 3 — Icon (emoji)",   type:"text",     hint:'e.g. 💳' },
          { key:"ocFeat3Text",    label:"Feature 3 — Text",           type:"text",     hint:'e.g. "Secure payment via Razorpay"' },
          { key:"ocFeat4Icon",    label:"Feature 4 — Icon (emoji)",   type:"text",     hint:'e.g. 📋' },
          { key:"ocFeat4Text",    label:"Feature 4 — Text",           type:"text",     hint:'e.g. "Digital prescription delivered instantly"' },
          { key:"ocCtaText",      label:"CTA Button Text",            type:"text",     hint:'e.g. "Book Online Consultation"' },
          { key:"ocCard1Icon",    label:"Side Card 1 — Icon",         type:"text",     hint:'e.g. 🕐' },
          { key:"ocCard1Title",   label:"Side Card 1 — Title",        type:"text",     hint:'e.g. "24-hr Confirmation"' },
          { key:"ocCard1Desc",    label:"Side Card 1 — Description",  type:"textarea", hint:'e.g. "We confirm your slot within 24 hours."' },
          { key:"ocCard2Icon",    label:"Side Card 2 — Icon",         type:"text",     hint:'e.g. 🔒' },
          { key:"ocCard2Title",   label:"Side Card 2 — Title",        type:"text",     hint:'e.g. "Confidential & Secure"' },
          { key:"ocCard2Desc",    label:"Side Card 2 — Description",  type:"textarea" },
          { key:"ocCard3Icon",    label:"Side Card 3 — Icon",         type:"text",     hint:'e.g. 🌿' },
          { key:"ocCard3Title",   label:"Side Card 3 — Title",        type:"text",     hint:'e.g. "Classical Ayurveda"' },
          { key:"ocCard3Desc",    label:"Side Card 3 — Description",  type:"textarea" },
          { key:"ocCard4Icon",    label:"Side Card 4 — Icon",         type:"text",     hint:'e.g. 📱' },
          { key:"ocCard4Title",   label:"Side Card 4 — Title",        type:"text",     hint:'e.g. "Any Device"' },
          { key:"ocCard4Desc",    label:"Side Card 4 — Description",  type:"textarea" },
        ],
      },
      { id:"testimonials", title:"Patient Testimonials (Words from Our Patients)",
        hint:"Edit the rotating patient story cards shown on the home page.",
        fields:[
          { key:"testiSectionTag",   label:"Section Tag",             type:"text",     hint:'e.g. "Patient Stories"' },
          { key:"testiSectionTitle", label:"Section Heading",         type:"text",     hint:'e.g. "Words from Our Patients"' },
          { key:"testiSectionSub",   label:"Section Sub-heading",     type:"textarea", hint:'e.g. "Real experiences from people who found healing..."' },
          { key:"testi1Name",  label:"Testimonial 1 — Name",          type:"text",     hint:'e.g. "Meera Sharma"' },
          { key:"testi1Loc",   label:"Testimonial 1 — Location",      type:"text",     hint:'e.g. "Pune"' },
          { key:"testi1Text",  label:"Testimonial 1 — Quote",         type:"textarea" },
          { key:"testi2Name",  label:"Testimonial 2 — Name",          type:"text" },
          { key:"testi2Loc",   label:"Testimonial 2 — Location",      type:"text" },
          { key:"testi2Text",  label:"Testimonial 2 — Quote",         type:"textarea" },
          { key:"testi3Name",  label:"Testimonial 3 — Name",          type:"text" },
          { key:"testi3Loc",   label:"Testimonial 3 — Location",      type:"text" },
          { key:"testi3Text",  label:"Testimonial 3 — Quote",         type:"textarea" },
          { key:"testi4Name",  label:"Testimonial 4 — Name",          type:"text" },
          { key:"testi4Loc",   label:"Testimonial 4 — Location",      type:"text" },
          { key:"testi4Text",  label:"Testimonial 4 — Quote",         type:"textarea" },
        ],
      },
    ],
  },

  about: {
    label:"ℹ️ About",
    groups:[
      { id:"about-hero", title:"Page Hero",
        hint:"The top banner of the About page.",
        fields:[
          { key:"aboutPageTag",        label:"Section Tag",         type:"text",     hint:'e.g. "Who We Are"' },
          { key:"title",               label:"Page Title (H1)",     type:"text"      },
          { key:"description",         label:"Hero Paragraph",      type:"textarea"  },
        ],
      },
      { id:"about-cards", title:"Vision & Mission Cards",
        fields:[
          { key:"visionTag",           label:"Vision Card — Tag",   type:"text",     hint:'e.g. "Our Vision"' },
          { key:"visionCardTitle",     label:"Vision Card — Title", type:"text",     hint:'e.g. "Healing Through Holistic Care"' },
          { key:"vision",              label:"Vision Card — Body",  type:"textarea"  },
          { key:"missionTag",          label:"Mission Card — Tag",  type:"text",     hint:'e.g. "Our Mission"' },
          { key:"missionCardTitle",    label:"Mission Card — Title",type:"text",     hint:'e.g. "Patient-Centered Wellness"' },
          { key:"mission",             label:"Mission Card — Body", type:"textarea"  },
        ],
      },
      { id:"about-meta", title:"Other Details",
        fields:[
          { key:"established",         label:"Established Year",    type:"text"      },
          { key:"teamTitle",           label:"Team Section Title",  type:"text"      },
        ],
      },
      { id:"about-sections", title:"Section Labels & Headings",
        hint:"All the section tags, headings, and static labels across the About page.",
        fields:[
          { key:"coreValuesTag",       label:"Core Values Section Tag",       type:"text",     hint:'e.g. "What We Stand For"' },
          { key:"coreValuesHeading",   label:"Core Values Heading",           type:"text",     hint:'e.g. "Our Core Values"' },
          { key:"whyChooseUsTag",      label:"Why Choose Us Section Tag",     type:"text",     hint:'e.g. "Why Choose Us"' },
          { key:"whyChooseUsHeading",  label:"Why Choose Us Heading",         type:"text",     hint:'e.g. "What Sets Us Apart"' },
          { key:"aboutCtaHeading",     label:"CTA Section Heading",           type:"text",     hint:'e.g. "Ready to Begin Your Healing?"' },
          { key:"aboutCtaSub",         label:"CTA Sub-text",                  type:"textarea", hint:'e.g. "Book a consultation and experience the difference..."' },
          { key:"aboutCtaBtn",         label:"CTA Button Text",               type:"text",     hint:'e.g. "Book Appointment"' },
        ],
      },
      { id:"about-values", title:"Core Values & Why Choose Us",
        hint:"The 4 core value cards and the Why Choose Us bullet list.",
        fields:[
          { key:"value1",  label:"Core Value 1",    type:"text", hint:'e.g. "Authenticity in Ayurveda"' },
          { key:"value2",  label:"Core Value 2",    type:"text", hint:'e.g. "Ethical medical practice"' },
          { key:"value3",  label:"Core Value 3",    type:"text", hint:'e.g. "Personalized patient care"' },
          { key:"value4",  label:"Core Value 4",    type:"text", hint:'e.g. "Continuous learning & growth"' },
          { key:"whyChooseUs1", label:"Why Choose Us — Point 1", type:"text", hint:'e.g. "Classical Ayurvedic diagnosis & nadi pariksha"' },
          { key:"whyChooseUs2", label:"Why Choose Us — Point 2", type:"text", hint:'e.g. "Experienced and qualified BAMS physicians"' },
          { key:"whyChooseUs3", label:"Why Choose Us — Point 3", type:"text", hint:'e.g. "Safe, evidence-based therapies"' },
          { key:"whyChooseUs4", label:"Why Choose Us — Point 4", type:"text", hint:'e.g. "Patient education and lifestyle guidance"' },
        ],
      },
    ],
  },

  services: {
    label:"🏥 Services",
    groups:[
      { id:"svc-header", title:"Page Header",
        fields:[
          { key:"svcTag",    label:"Section Tag",      type:"text",     hint:'e.g. "Our Specialities"' },
          { key:"title",     label:"Page Title (H1)",  type:"text"      },
          { key:"intro",     label:"Introduction Text",type:"textarea"  },
        ],
      },
      { id:"svc-cards", title:"Service Cards",
        hint:"Each entry becomes one card on the Services page.",
        fields:[
          { key:"service1",     label:"Service 1 — Title",       type:"text"      },
          { key:"service1Desc", label:"Service 1 — Description", type:"textarea"  },
          { key:"service2",     label:"Service 2 — Title",       type:"text"      },
          { key:"service2Desc", label:"Service 2 — Description", type:"textarea"  },
          { key:"service3",     label:"Service 3 — Title",       type:"text"      },
          { key:"service3Desc", label:"Service 3 — Description", type:"textarea"  },
          { key:"service4",     label:"Service 4 — Title",       type:"text"      },
          { key:"service4Desc", label:"Service 4 — Description", type:"textarea"  },
          { key:"service5",     label:"Service 5 — Title",       type:"text"      },
          { key:"service5Desc", label:"Service 5 — Description", type:"textarea"  },
          { key:"service6",     label:"Service 6 — Title",       type:"text"      },
          { key:"service6Desc", label:"Service 6 — Description", type:"textarea"  },
        ],
      },
      { id:"svc-stats", title:"Statistics Band",
        hint:"The green stats band below the service cards.",
        fields:[
          { key:"statBand1N", label:"Stat 1 — Number", type:"text", hint:'e.g. "100%"' },
          { key:"statBand1L", label:"Stat 1 — Label",  type:"text", hint:'e.g. "Natural Ingredients"' },
          { key:"statBand2N", label:"Stat 2 — Number", type:"text", hint:'e.g. "5000+"' },
          { key:"statBand2L", label:"Stat 2 — Label",  type:"text", hint:'e.g. "Years of Wisdom"' },
          { key:"statBand3N", label:"Stat 3 — Number", type:"text", hint:'e.g. "25+"' },
          { key:"statBand3L", label:"Stat 3 — Label",  type:"text", hint:'e.g. "Therapies Available"' },
          { key:"statBand4N", label:"Stat 4 — Number", type:"text", hint:'e.g. "Safe"' },
          { key:"statBand4L", label:"Stat 4 — Label",  type:"text", hint:'e.g. "Evidence-Based Care"' },
        ],
      },
      { id:"svc-cta", title:"Call-to-Action Section",
        hint:"The bottom CTA area on the Services page.",
        fields:[
          { key:"svcCtaHeading", label:"CTA Heading",         type:"text",     hint:'e.g. "Begin Your Healing Journey Today"' },
          { key:"svcCtaSub",     label:"CTA Sub-text",        type:"textarea", hint:'e.g. "Schedule a consultation..."' },
          { key:"svcCtaBtn",     label:"Primary CTA Button",  type:"text",     hint:'e.g. "Book Appointment"' },
          { key:"svcCtaBtn2",    label:"Secondary CTA Button",type:"text",     hint:'e.g. "Learn About Us"' },
        ],
      },
    ],
  },

  "our-doctors": {
    label:"👨‍⚕️ Our Doctors",
    groups:[
      { id:"od-hero", title:"Page Hero",
        hint:"The top section of the Our Doctors listing page.",
        fields:[
          { key:"odSectionTag",  label:"Section Tag",                type:"text",     hint:'e.g. "Meet Our Physicians"' },
          { key:"odTitle",       label:"Page Heading (H1)",          type:"text",     hint:'e.g. "Our Doctors"' },
          { key:"odSubtitle",    label:"Sub-heading Paragraph",      type:"textarea", hint:"Shown below the headline" },
          { key:"odFlipHint",    label:"Flip Card Hint Text",        type:"text",     hint:"e.g. Tap any card to flip and read the doctor's health insight"  },
          { key:"odEmptyText",   label:"Empty State Text",           type:"text",     hint:'Shown when no doctor profiles exist, e.g. "Doctor profiles coming soon."' },
        ],
      },
      { id:"od-card-labels", title:"Doctor Card — Field Labels",
        hint:"Customise the label text shown on each doctor's profile card (e.g. if you want 'Consultation Fee' to say 'Fee' instead).",
        fields:[
          { key:"timingLabel",    label:"Timing Field Label",    type:"text", hint:'e.g. "Timing"' },
          { key:"feeLabel",       label:"Fee Field Label",       type:"text", hint:'e.g. "Fee"' },
          { key:"specialtyLabel", label:"Specialty Field Label", type:"text", hint:'e.g. "Specialty"' },
          { key:"bioLabel",       label:"Bio Field Label",       type:"text", hint:'e.g. "Bio"' },
          { key:"expSuffix",      label:"Experience Suffix",     type:"text", hint:'e.g. "+ years experience"' },
        ],
      },
      { id:"od-cta", title:"Call-to-Action Section",
        fields:[
          { key:"odCtaHeading",  label:"CTA Heading",                type:"text",     hint:'e.g. "Ready to Begin Your Healing?"' },
          { key:"odCtaSub",      label:"CTA Sub-text",               type:"textarea", hint:'e.g. "Book a consultation with our expert physicians today."' },
          { key:"odCtaBtn",      label:"CTA Button Text",            type:"text",     hint:'e.g. "Book Appointment"' },
        ],
      },
    ],
  },

  contact: {
    label:"📞 Contact",
    groups:[
      { id:"contact-heading", title:"Page Heading",
        fields:[
          { key:"contactPageTitle", label:"Page Title",    type:"text", hint:'e.g. "Contact Us"' },
          { key:"contactPageTag",   label:"Section Tag",   type:"text", hint:'e.g. "Get In Touch"' },
          { key:"contactIntro",     label:"Intro Text",    type:"textarea" },
        ],
      },
      { id:"contact-fields", title:"Contact Details",
        hint:"Labels AND values — both editable.",
        fields:[
          { key:"addressLabel",  label:"Address Field Label",   type:"text", hint:'e.g. "Address"' },
          { key:"address",       label:"Clinic Address",        type:"textarea" },
          { key:"phoneLabel",    label:"Phone Field Label",     type:"text", hint:'e.g. "Phone"' },
          { key:"phone",         label:"Phone Number",          type:"text" },
          { key:"emailLabel",    label:"Email Field Label",     type:"text", hint:'e.g. "Email"' },
          { key:"email",         label:"Email Address",         type:"text", hint:"Shown in the Contact strip on the homepage & Contact page — e.g. info@herambaayurveda.in" },
          { key:"whatsappLabel", label:"WhatsApp Field Label",  type:"text", hint:'e.g. "WhatsApp"' },
          { key:"whatsapp",      label:"WhatsApp Number",       type:"text" },
          { key:"timingLabel",   label:"Hours Field Label",     type:"text", hint:'e.g. "Hours"' },
          { key:"timing",        label:"Clinic Hours",          type:"textarea" },
          { key:"mapEmbed",      label:"Google Map Embed URL",  type:"text" },
        ],
      },
    ],
  },
};


/* ═══════════════════════════════════════════════════════════
   SEO PAGE CONFIG
═══════════════════════════════════════════════════════════ */
const SEO_FIELDS = [
  {
    id: "basic", title: "Page title & description",
    hint: "These appear in Google search results. Title: 50–60 chars. Description: 120–155 chars.",
    fields: [
      { key:"seoTitle",       label:"Page title",       type:"text",     hint:"50–60 characters ideal." },
      { key:"seoDescription", label:"Meta description", type:"textarea", hint:"120–155 characters ideal. Shown under your title in Google." },
      { key:"seoKeywords",    label:"Keywords",         type:"textarea", hint:"Comma-separated. e.g. Ayurveda clinic, Panchakarma, Ulhasnagar Ayurveda" },
      { key:"seoAuthor",      label:"Author",           type:"text",     hint:"e.g. Heramba Ayurveda" },
      { key:"seoCanonical",   label:"Canonical URL",    type:"text",     hint:"e.g. https://herambaayurveda.in/" },
    ],
  },
  {
    id: "og", title: "Open Graph (WhatsApp / Facebook preview)",
    hint: "Controls what appears when your link is shared on WhatsApp, Facebook, LinkedIn.",
    fields: [
      { key:"ogTitle",       label:"OG title",       type:"text",     hint:"Usually same as page title" },
      { key:"ogDescription", label:"OG description", type:"textarea", hint:"Usually same as meta description" },
      { key:"ogImage",       label:"OG image URL",   type:"text",     hint:"Full URL. Must be 1200x630px. e.g. https://herambaayurveda.in/og-image.jpg" },
    ],
  },
  {
    id: "geo", title: "Geo & local SEO",
    hint: "Helps Google show your site in local search results for Ulhasnagar / Maharashtra.",
    fields: [
      { key:"geoRegion",    label:"Geo region",    type:"text", hint:"e.g. IN-MH" },
      { key:"geoPlacename", label:"Geo placename", type:"text", hint:"e.g. Ulhasnagar, Maharashtra" },
    ],
  },
  {
    id: "schema", title: "Schema.org clinic info",
    hint: "Structured data Google uses for rich results and Knowledge Panel.",
    fields: [
      { key:"schemaPhone",     label:"Phone",         type:"text", hint:"e.g. +91-9689469704" },
      { key:"schemaEmail",     label:"Email",         type:"text", hint:"e.g. herambaayurveda@gmail.com" },
      { key:"schemaCity",      label:"City",          type:"text", hint:"e.g. Ulhasnagar" },
      { key:"schemaState",     label:"State",         type:"text", hint:"e.g. Maharashtra" },
      { key:"schemaPin",       label:"PIN code",      type:"text", hint:"e.g. 421004" },
      { key:"schemaInstagram", label:"Instagram URL", type:"text", hint:"Clean URL only — no ?igsh= tracking." },
      { key:"schemaPriceRange",label:"Price range",   type:"text", hint:"e.g. ₹₹" },
    ],
  },
];

/* ═══════════════════════════════════════════════════════════
   LIVE PREVIEW COMPONENTS
   Every single visible string is driven by d.xxx fields.
   No hardcoded visible text anywhere.
═══════════════════════════════════════════════════════════ */

function PvHero({ d }) {
  const sanskrit   = d.heroSanskrit  || "ॐ सर्वे भवन्तु सुखिनः · May all beings be well";
  const title = d.heroTitle || "Heramba Ayurveda,\nTimeless Healing";
  const sub        = d.heroSubtitle  || "Rooted in 5,000 years of Vedic wisdom —\ndelivered with warmth and modern care.";
  const cta        = d.ctaText       || "Book Appointment";
  const ctaSec     = d.ctaSecondary  || "View Treatments";
  const badge1     = d.badge1        || "⭐ 4.9 / 5 Rating";
  const badge2     = d.badge2        || `🏥 Est. ${d.established||"2012"}`;
  const badge3     = d.badge3        || "🌿 100% Natural";
  const badge4     = d.badge4        || "🔬 Evidence-Based";
  return (
    <div style={{ padding:"26px 18px 28px", textAlign:"center", position:"relative",
      overflow:"hidden", borderRadius:10,
      background:`linear-gradient(145deg,${C.cream} 0%,${C.creamD} 45%,${C.saffronL} 100%)` }}>
      <div style={{ position:"absolute", right:-14, top:-14, opacity:0.04, fontSize:100, pointerEvents:"none" }}>✿</div>
      {/* Sanskrit line */}
      <p style={{ fontSize:7.5, letterSpacing:"0.15em", color:C.saffronD,
        fontFamily:"serif", fontStyle:"italic", marginBottom:8, opacity:0.85 }}>
        {sanskrit}
      </p>
      {/* H1 */}
      <h1 style={{ fontFamily:"Georgia,serif", fontSize:17, fontWeight:800, color:C.ink,
        lineHeight:1.2, marginBottom:8, whiteSpace:"pre-line" }}>{title}</h1>
      {/* Divider */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginBottom:8 }}>
        <div style={{ height:1, width:28, background:`linear-gradient(90deg,transparent,${C.saffron})` }}/>
        <MiniLotus size={15} color={C.saffron}/>
        <div style={{ height:1, width:28, background:`linear-gradient(90deg,${C.saffron},transparent)` }}/>
      </div>
      {/* Subtitle */}
      <p style={{ fontSize:8.5, color:C.inkMid, lineHeight:1.75, fontFamily:"Georgia,serif",
        whiteSpace:"pre-line", maxWidth:280, margin:"0 auto 13px" }}>{sub}</p>
      {/* CTAs */}
      <div style={{ display:"flex", gap:7, justifyContent:"center", flexWrap:"wrap", marginBottom:10 }}>
        <span style={{ padding:"6px 16px", borderRadius:30, fontSize:9, fontWeight:700,
          background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
          color:"#fff", boxShadow:`0 4px 10px rgba(232,146,42,0.38)` }}>
          🌿 {cta}
        </span>
        <span style={{ padding:"6px 16px", borderRadius:30, fontSize:9, fontWeight:600,
          border:`1.5px solid ${C.saffron}`, color:C.saffron }}>
          {ctaSec}
        </span>
      </div>
      {/* Badges */}
      <div style={{ display:"flex", gap:5, justifyContent:"center", flexWrap:"wrap" }}>
        {[badge1, badge2, badge3, badge4].map((b,i)=>(
          <span key={i} style={{ fontSize:7.5, fontWeight:600, color:C.inkMid,
            background:"rgba(255,255,255,0.72)", borderRadius:20, padding:"3px 7px" }}>{b}</span>
        ))}
      </div>
    </div>
  );
}

function PvAbout({ d }) {
  const tag      = d.aboutSectionTag || "About Our Clinic";
  const heading1 = d.aboutHeading1   || "Healing Rooted in";
  const heading2 = d.aboutHeading2   || "Classical Wisdom";
  const summary  = d.aboutSummary    || "At our clinic we practise pure, classical Ayurveda — every treatment personalised to your constitution.";
  const b1       = d.aboutBullet1    || "Classical nadi pariksha and pulse diagnosis";
  const b2       = d.aboutBullet2    || "Authentic Ashtanga Hridayam protocols";
  const b3       = d.aboutBullet3    || "Zero synthetic additives in all formulations";
  const cta      = d.aboutCta        || "Learn More →";
  const estLabel = d.aboutEstLabel   || "Established";
  const est      = d.established     || "2012";
  return (
    <div style={{ background:"#fff", padding:"16px", borderRadius:10,
      display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(260px,100%),1fr))", gap:12, alignItems:"center" }}>
      {/* Illustration + floating badge */}
      <div style={{ display:"flex", justifyContent:"center", position:"relative" }}>
        <div style={{ width:76, height:76, borderRadius:"50%",
          background:`radial-gradient(${C.saffronL},${C.creamD})`,
          display:"flex", alignItems:"center", justifyContent:"center", fontSize:30 }}>🌿</div>
        <div style={{ position:"absolute", bottom:-4, right:8,
          background:"#fff", borderRadius:12, padding:"5px 8px",
          boxShadow:"0 4px 12px rgba(0,0,0,0.10)", border:`1px solid ${C.saffronL}`,
          display:"flex", alignItems:"center", gap:5 }}>
          <MiniLotus size={14} color={C.saffron}/>
          <div>
            <div style={{ fontSize:7, color:C.inkLight, fontFamily:"sans-serif" }}>{estLabel}</div>
            <div style={{ fontSize:11, fontWeight:700, color:C.ink, fontFamily:"Georgia,serif" }}>{est}</div>
          </div>
        </div>
      </div>
      {/* Text */}
      <div>
        <p style={{ fontSize:7, fontWeight:700, letterSpacing:"0.12em", color:C.saffronD,
          textTransform:"uppercase", marginBottom:4, fontFamily:"sans-serif" }}>{tag}</p>
        <h2 style={{ fontFamily:"Georgia,serif", fontSize:11, fontWeight:700,
          color:C.ink, lineHeight:1.3, marginBottom:5 }}>
          {heading1}<br/><span style={{ color:C.saffronD }}>{heading2}</span>
        </h2>
        <p style={{ fontSize:7.5, color:C.inkMid, lineHeight:1.7,
          fontFamily:"Georgia,serif", marginBottom:6 }}>
          {summary.length>120 ? summary.slice(0,120)+"…" : summary}
        </p>
        {[b1,b2,b3].map((pt,i)=>(
          <div key={i} style={{ display:"flex", gap:5, alignItems:"center", marginBottom:3 }}>
            <div style={{ width:10, height:10, borderRadius:"50%", flexShrink:0,
              background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
              display:"flex", alignItems:"center", justifyContent:"center" }}>
              <svg width="5" height="5" viewBox="0 0 10 10">
                <path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none"/>
              </svg>
            </div>
            <span style={{ fontSize:7, color:C.inkMid, fontFamily:"sans-serif" }}>{pt}</span>
          </div>
        ))}
        <div style={{ marginTop:7 }}>
          <span style={{ padding:"5px 12px", borderRadius:20, fontSize:8, fontWeight:700,
            background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
            color:"#fff" }}>{cta}</span>
        </div>
      </div>
    </div>
  );
}

function PvStats({ d }) {
  const stats = [
    { n:d.statPatients||"1200",     label:d.statPatientsLabel||"Happy Patients"     },
    { n:d.statYears||"12",          label:d.statYearsLabel||"Years of Practice"     },
    { n:d.statTreatments||"25",     label:d.statTreatmentsLabel||"Treatments Offered"},
    { n:d.statDoctors||"4",         label:d.statDoctorsLabel||"Expert Doctors"       },
  ];
  return (
    <div style={{ background:`linear-gradient(135deg,${C.sageD},${C.sage})`,
      padding:"15px 10px", borderRadius:10, position:"relative", overflow:"hidden",
      display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:6, textAlign:"center" }}>
      <div style={{ position:"absolute", right:-12, top:-12, opacity:0.05, fontSize:70 }}>✿</div>
      {stats.map((s,i)=>(
        <div key={i}>
          <div style={{ fontFamily:"Georgia,serif", fontSize:14, fontWeight:800,
            color:C.turmeric, lineHeight:1 }}>{s.n}+</div>
          <div style={{ fontSize:6.5, color:"rgba(255,255,255,0.72)", marginTop:2,
            fontFamily:"sans-serif" }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

function PvTreatments({ d }) {
  const tag      = d.treatmentsTag     || "Our Specialities";
  const heading  = d.treatmentsHeading || "Treatments We Offer";
  const sub      = d.treatmentsSub     || "Time-tested Ayurvedic therapies, each adapted to your constitution.";
  const cta      = d.treatmentsCta     || "See All Treatments →";
  const cards = [
    { icon:"🩺", title:d.t1title||"Ayurvedic Consultation", desc:d.t1desc||"Classical nadi pariksha and dosha assessment." },
    { icon:"🌸", title:d.t2title||"Pañcakarma Therapy",     desc:d.t2desc||"Five-fold detoxification and rejuvenation." },
    { icon:"💆", title:d.t3title||"Shirodhara",             desc:d.t3desc||"Warm medicated oil for profound mental calm." },
    { icon:"🥗", title:d.t4title||"Diet & Lifestyle",       desc:d.t4desc||"Prakṛti-based nutrition and seasonal routine." },
    { icon:"🧘", title:d.t5title||"Yoga & Pranayama",       desc:d.t5desc||"Breath-led movement for your constitution." },
    { icon:"🌾", title:d.t6title||"Herbal Medicines",       desc:d.t6desc||"Authentic formulations — zero synthetic additives." },
  ];
  const bgs = [C.sageL, C.saffronL, "#F0F4FF", C.sageL, "#FFF5F0", C.saffronL];
  return (
    <div style={{ background:C.cream, padding:"13px", borderRadius:10 }}>
      {/* Section header */}
      <div style={{ textAlign:"center", marginBottom:10 }}>
        <span style={{ fontSize:7, fontWeight:700, letterSpacing:"0.12em", color:C.saffronD,
          textTransform:"uppercase", fontFamily:"sans-serif",
          background:C.saffronL, borderRadius:20, padding:"2px 8px" }}>{tag}</span>
        <p style={{ fontFamily:"Georgia,serif", fontSize:12, fontWeight:700,
          color:C.ink, margin:"4px 0 3px" }}>{heading}</p>
        <p style={{ fontSize:7.5, color:C.inkMid, fontFamily:"Georgia,serif",
          lineHeight:1.5, maxWidth:260, margin:"0 auto" }}>
          {sub.length>80 ? sub.slice(0,80)+"…" : sub}
        </p>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:5 }}>
        {cards.map((c,i)=>(
          <div key={i} style={{ background:"#fff", borderRadius:7, padding:"8px 7px",
            border:`1px solid ${C.saffronL}`, boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
            <div style={{ width:22, height:22, borderRadius:5, background:bgs[i],
              display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, marginBottom:4 }}>{c.icon}</div>
            <p style={{ fontFamily:"Georgia,serif", fontSize:7.5, fontWeight:700,
              color:C.ink, marginBottom:2, lineHeight:1.25 }}>{c.title}</p>
            <p style={{ fontSize:7, color:C.inkLight, lineHeight:1.4, fontFamily:"sans-serif" }}>
              {c.desc.length>55 ? c.desc.slice(0,55)+"…" : c.desc}
            </p>
          </div>
        ))}
      </div>
      <div style={{ textAlign:"center", marginTop:8 }}>
        <span style={{ fontSize:8, fontWeight:600, color:C.sageD,
          border:`1px solid ${C.sage}`, borderRadius:20, padding:"4px 12px" }}>{cta}</span>
      </div>
    </div>
  );
}

function PvTrust({ d }) {
  const tag       = d.trustSectionTag || "Our Promise";
  const text      = d.trustText       || "Trusted by over a thousand patients for safe, authentic, and holistic Ayurvedic healing.";
  const ctaLabel  = d.trustCtaLabel   || "Call Us:";
  const phone     = d.phone           || "";
  return (
    <div style={{ background:`linear-gradient(135deg,${C.saffronL},${C.creamD})`,
      padding:"18px", borderRadius:10, textAlign:"center",
      borderTop:`3px solid ${C.saffron}20`, borderBottom:`3px solid ${C.saffron}20` }}>
      {tag && (
        <span style={{ fontSize:7, fontWeight:700, letterSpacing:"0.12em",
          color:C.saffronD, textTransform:"uppercase", fontFamily:"sans-serif",
          background:"rgba(255,255,255,0.6)", borderRadius:20, padding:"2px 10px",
          display:"inline-block", marginBottom:8 }}>{tag}</span>
      )}
      <div style={{ display:"block" }}><MiniLotus size={20} color={C.saffron}/></div>
      <p style={{ fontSize:9, color:C.inkMid, lineHeight:1.8, marginTop:6,
        fontFamily:"Georgia,serif", fontStyle:"italic", maxWidth:290, margin:"6px auto 0" }}>
        {text}
      </p>
      {phone && (
        <div style={{ marginTop:10 }}>
          <span style={{ padding:"6px 14px", borderRadius:30, fontSize:8.5, fontWeight:700,
            background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
            color:"#fff", boxShadow:`0 3px 10px rgba(232,146,42,0.35)` }}>
            📞 {ctaLabel} {phone}
          </span>
        </div>
      )}
    </div>
  );
}

function PvAboutPage({ d }) {
  const pageTag   = d.aboutPageTag      || "Who We Are";
  const vTag      = d.visionTag         || "Our Vision";
  const vTitle    = d.visionCardTitle   || "Healing Through Holistic Care";
  const vision    = d.vision            || "To heal through holistic, natural, and personalized Ayurvedic care.";
  const mTag      = d.missionTag        || "Our Mission";
  const mTitle    = d.missionCardTitle  || "Patient-Centered Wellness";
  const mission   = d.mission           || "To deliver patient-centered treatments through authentic classical practices.";
  return (
    <div style={{ borderRadius:10, overflow:"hidden", background:C.cream }}>
      <div style={{ padding:"20px 16px", textAlign:"center",
        background:`linear-gradient(145deg,${C.cream},${C.creamD} 45%,${C.saffronL})` }}>
        <span style={{ fontSize:7, fontWeight:700, letterSpacing:"0.12em", color:C.saffronD,
          textTransform:"uppercase", fontFamily:"sans-serif",
          background:C.saffronL, borderRadius:20, padding:"2px 8px" }}>{pageTag}</span>
        <h1 style={{ fontFamily:"Georgia,serif", fontSize:14, fontWeight:800,
          color:C.ink, margin:"6px 0 5px" }}>{d.title||"About Heramba Ayurveda"}</h1>
        <p style={{ fontSize:8, color:C.inkMid, fontFamily:"Georgia,serif",
          lineHeight:1.7, maxWidth:290, margin:"0 auto" }}>
          {(d.description||"We practice authentic Ayurveda rooted in classical wisdom...").slice(0,110)}…
        </p>
      </div>
      <div style={{ padding:"10px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
        {[
          { tag:vTag, title:vTitle, icon:"🔭", text:vision,  bg:C.saffronL, border:C.saffron },
          { tag:mTag, title:mTitle, icon:"🌱", text:mission, bg:C.sageL,    border:C.sage    },
        ].map((card,i)=>(
          <div key={i} style={{ background:card.bg, borderRadius:8, padding:"10px",
            border:`1.5px solid ${card.border}30` }}>
            <div style={{ fontSize:16, marginBottom:4 }}>{card.icon}</div>
            <span style={{ fontSize:7, fontWeight:700, letterSpacing:"0.1em",
              color:card.border === C.saffron ? C.saffronD : C.sageD,
              textTransform:"uppercase", fontFamily:"sans-serif" }}>{card.tag}</span>
            <p style={{ fontSize:8.5, fontWeight:700, color:C.ink, margin:"3px 0",
              fontFamily:"Georgia,serif", lineHeight:1.25 }}>{card.title}</p>
            <p style={{ fontSize:7.5, color:C.inkMid, lineHeight:1.6, fontFamily:"sans-serif" }}>
              {card.text.slice(0,80)}…
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PvServicesPage({ d }) {
  const tag      = d.svcTag  || "Our Specialities";
  const services = [1,2,3,4,5,6].map(n=>d[`service${n}`]).filter(Boolean);
  const icons    = ["🌿","🧘","🌸","🍵","✨","🌺"];
  const colBgs   = [C.sageL, C.saffronL, "#FFF5F0", C.sageL, C.saffronL, "#F0F4FF"];
  const colLines = [C.sage, C.saffron, C.terra, C.sage, C.saffron, "#9BB5E8"];
  return (
    <div style={{ borderRadius:10, overflow:"hidden", background:C.cream }}>
      <div style={{ padding:"18px 16px", textAlign:"center",
        background:`linear-gradient(145deg,${C.cream},${C.creamD},${C.saffronL})` }}>
        <span style={{ fontSize:7, fontWeight:700, letterSpacing:"0.12em", color:C.saffronD,
          textTransform:"uppercase", fontFamily:"sans-serif",
          background:C.saffronL, borderRadius:20, padding:"2px 8px" }}>{tag}</span>
        <h1 style={{ fontFamily:"Georgia,serif", fontSize:14, fontWeight:800,
          color:C.ink, margin:"5px 0 4px" }}>{d.title || "Treatments We Offer"}</h1>
        <p style={{ fontSize:8, color:C.inkMid, fontFamily:"Georgia,serif",
          lineHeight:1.7, maxWidth:270, margin:"0 auto" }}>
          {(d.intro||"Time-tested Ayurvedic therapies adapted to your constitution.").slice(0,90)}
        </p>
      </div>
      <div style={{ padding:"10px", display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6 }}>
        {(services.length ? services : ["Ayurvedic Consultation","Pañcakarma","Shirodhara","Diet","Yoga","Herbal Medicines"])
          .slice(0,6).map((s,i)=>(
          <div key={i} style={{ background:"#fff", borderRadius:8, padding:"10px",
            border:`1px solid ${colBgs[i]}`, position:"relative", overflow:"hidden",
            boxShadow:"0 2px 6px rgba(0,0,0,0.04)" }}>
            <div style={{ position:"absolute", top:-10, right:-10, width:34,
              height:34, borderRadius:"50%", background:colBgs[i], opacity:0.8 }}/>
            <div style={{ width:22, height:22, borderRadius:5, background:colBgs[i],
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:11, marginBottom:5 }}>{icons[i]}</div>
            <p style={{ fontFamily:"Georgia,serif", fontSize:8, fontWeight:700,
              color:C.ink, lineHeight:1.3 }}>{s}</p>
            <div style={{ height:2, width:16, borderRadius:1, marginTop:4,
              background:`linear-gradient(90deg,${colLines[i]},transparent)` }}/>
          </div>
        ))}
      </div>
    </div>
  );
}

function PvDoctorPage({ d }) {
  const expSuffix    = d.expSuffix      || "+ years experience";
  const timingLabel  = d.timingLabel    || "Timing";
  const feeLabel     = d.feeLabel       || "Fee";
  const specLabel    = d.specialtyLabel || "Specialty";
  const bioLabel     = d.bioLabel       || "Bio";
  return (
    <div style={{ borderRadius:10, overflow:"hidden", background:C.cream }}>
      <div style={{ background:`linear-gradient(135deg,${C.sageD},${C.sage})`,
        padding:"18px 16px", textAlign:"center" }}>
        <div style={{ width:44, height:44, borderRadius:"50%",
          background:`linear-gradient(135deg,${C.turmeric},${C.saffron})`,
          margin:"0 auto 7px", display:"flex", alignItems:"center",
          justifyContent:"center", fontSize:18 }}>🩺</div>
        <h1 style={{ fontFamily:"Georgia,serif", fontSize:13, fontWeight:800,
          color:"#fff", marginBottom:2 }}>Dr. {d.name||"Doctor Name"}</h1>
        <p style={{ fontSize:8, color:"rgba(255,255,255,0.8)", fontFamily:"sans-serif" }}>
          {d.qualification||"BAMS"} · {d.experience||"10"}{expSuffix}
        </p>
      </div>
      <div style={{ padding:"10px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
        {[
          { icon:"🕐", label:timingLabel, val: d.timing      || "9am – 6pm" },
          { icon:"💰", label:feeLabel,    val: d.consultFee  || "₹500" },
          { icon:"🏥", label:specLabel,   val: (d.specialization||"General Ayurveda").slice(0,35) },
          { icon:"📝", label:bioLabel,    val: (d.bio||"Expert in classical Ayurvedic protocols.").slice(0,50)+"…" },
        ].map(item=>(
          <div key={item.label} style={{ background:"#fff", borderRadius:7, padding:"8px",
            border:`1px solid ${C.saffronL}`, display:"flex", gap:5, alignItems:"flex-start" }}>
            <span style={{ fontSize:12, flexShrink:0 }}>{item.icon}</span>
            <div>
              <p style={{ fontSize:6.5, fontWeight:700, color:C.saffronD,
                textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:2, fontFamily:"sans-serif" }}>
                {item.label}
              </p>
              <p style={{ fontSize:7.5, color:C.inkMid, lineHeight:1.4, fontFamily:"sans-serif" }}>
                {item.val}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PvContactPage({ d }) {
  const pageTitle    = d.contactPageTitle || "Contact Us";
  const pageTag      = d.contactPageTag   || "Get In Touch";
  const intro        = d.contactIntro     || "";
  const addressLabel = d.addressLabel     || "Address";
  const phoneLabel   = d.phoneLabel       || "Phone";
  const emailLabel   = d.emailLabel       || "Email";
  const waLabel      = d.whatsappLabel    || "WhatsApp";
  const timingLabel  = d.timingLabel      || "Hours";
  return (
    <div style={{ background:C.cream, borderRadius:10, padding:"14px" }}>
      <div style={{ textAlign:"center", marginBottom:10 }}>
        <span style={{ fontSize:7, fontWeight:700, letterSpacing:"0.12em", color:C.saffronD,
          textTransform:"uppercase", fontFamily:"sans-serif",
          background:C.saffronL, borderRadius:20, padding:"2px 8px" }}>{pageTag}</span>
        <h1 style={{ fontFamily:"Georgia,serif", fontSize:13, fontWeight:800,
          color:C.ink, margin:"5px 0 3px" }}>{pageTitle}</h1>
        {intro && <p style={{ fontSize:8, color:C.inkMid, fontFamily:"sans-serif" }}>{intro.slice(0,80)}</p>}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:5 }}>
        {[
          { icon:"📍", label:addressLabel, val: d.address  || "–" },
          { icon:"📞", label:phoneLabel,   val: d.phone    || "–" },
          { icon:"✉️",  label:emailLabel,  val: d.email    || "–" },
          { icon:"💬", label:waLabel,      val: d.whatsapp || "–" },
          { icon:"🕐", label:timingLabel,  val: d.timing   || "–" },
        ].map(item=>(
          <div key={item.label} style={{ background:"#fff", borderRadius:7, padding:"8px",
            border:`1px solid ${C.creamD}`, display:"flex", gap:5, alignItems:"flex-start" }}>
            <span style={{ fontSize:12, flexShrink:0 }}>{item.icon}</span>
            <div>
              <p style={{ fontSize:6.5, fontWeight:700, color:C.saffronD,
                textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:2, fontFamily:"sans-serif" }}>
                {item.label}
              </p>
              <p style={{ fontSize:7.5, color:C.inkMid, lineHeight:1.4, fontFamily:"sans-serif", wordBreak:"break-all" }}>
                {String(item.val).slice(0,50)}{String(item.val).length>50?"…":""}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Online Consultation Preview ── */
function PvOnlineConsult({ d }) {
  const heading1 = d.ocHeading1    || "Ayurvedic Care,";
  const heading2 = d.ocHeading2    || "Wherever You Are";
  const badge    = d.ocBadgeLabel  || "Available Worldwide";
  const desc     = d.ocDescription || "Connect with our experienced doctors via video call or phone. Get personalised Ayurvedic guidance, herbal prescriptions, and diet plans — all from the comfort of your home.";
  const cta      = d.ocCtaText     || "Book Online Consultation";
  const feats = [
    { icon: d.ocFeat1Icon||"📹", text: d.ocFeat1Text||"HD Video or Phone consultation" },
    { icon: d.ocFeat2Icon||"🌍", text: d.ocFeat2Text||"Works with any timezone worldwide" },
    { icon: d.ocFeat3Icon||"💳", text: d.ocFeat3Text||"Secure payment via Razorpay" },
    { icon: d.ocFeat4Icon||"📋", text: d.ocFeat4Text||"Digital prescription delivered instantly" },
  ];
  const cards = [
    { icon: d.ocCard1Icon||"🕐", title: d.ocCard1Title||"24-hr Confirmation",  desc: d.ocCard1Desc||"We confirm your slot within 24 hours of booking." },
    { icon: d.ocCard2Icon||"🔒", title: d.ocCard2Title||"Confidential & Secure",desc: d.ocCard2Desc||"All consultations and records are fully private." },
    { icon: d.ocCard3Icon||"🌿", title: d.ocCard3Title||"Classical Ayurveda",   desc: d.ocCard3Desc||"Same authentic protocols as our in-person clinic." },
    { icon: d.ocCard4Icon||"📱", title: d.ocCard4Title||"Any Device",           desc: d.ocCard4Desc||"Join from phone, tablet, or computer — no app needed." },
  ];
  return (
    <div style={{ background:"linear-gradient(135deg,#1a1a2e 0%,#16213e 50%,#0f3460 100%)",
      borderRadius:10, padding:"14px", overflow:"hidden" }}>
      <div style={{ display:"inline-flex", alignItems:"center", gap:5, marginBottom:8,
        background:"rgba(255,255,255,0.08)", borderRadius:20, padding:"3px 10px",
        border:"1px solid rgba(255,255,255,0.15)" }}>
        <span style={{ fontSize:9 }}>💻</span>
        <span style={{ fontSize:7, fontWeight:700, letterSpacing:"0.12em",
          color:"rgba(255,255,255,0.7)", textTransform:"uppercase", fontFamily:"sans-serif" }}>{badge}</span>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
        {/* Left */}
        <div>
          <h2 style={{ fontFamily:"Georgia,serif", fontSize:11, fontWeight:800,
            color:"#fff", lineHeight:1.2, margin:"0 0 6px" }}>
            {heading1}<br/>
            <span style={{ color:C.turmeric }}>{heading2}</span>
          </h2>
          <p style={{ fontSize:7.5, color:"rgba(255,255,255,0.65)", lineHeight:1.7,
            fontFamily:"Georgia,serif", margin:"0 0 8px" }}>
            {desc.length>120 ? desc.slice(0,120)+"…" : desc}
          </p>
          {feats.map((f,i)=>(
            <div key={i} style={{ display:"flex", alignItems:"center", gap:5, marginBottom:3 }}>
              <div style={{ width:16, height:16, borderRadius:4, flexShrink:0,
                background:"rgba(255,255,255,0.08)", display:"flex", alignItems:"center",
                justifyContent:"center", fontSize:8 }}>{f.icon}</div>
              <span style={{ fontSize:7, color:"rgba(255,255,255,0.7)", fontFamily:"sans-serif" }}>{f.text}</span>
            </div>
          ))}
          <div style={{ marginTop:8 }}>
            <span style={{ padding:"4px 10px", borderRadius:20, fontSize:8, fontWeight:700,
              background:`linear-gradient(135deg,${C.turmeric},${C.saffron})`, color:C.ink }}>
              🌿 {cta}
            </span>
          </div>
        </div>
        {/* Right */}
        <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
          {cards.map((card,i)=>(
            <div key={i} style={{ background:"rgba(255,255,255,0.06)",
              border:"1px solid rgba(255,255,255,0.1)", borderRadius:6, padding:"6px",
              display:"flex", gap:5 }}>
              <span style={{ fontSize:11, flexShrink:0 }}>{card.icon}</span>
              <div>
                <p style={{ margin:0, fontSize:7, fontWeight:700,
                  color:"rgba(255,255,255,0.9)", fontFamily:"sans-serif" }}>{card.title}</p>
                <p style={{ margin:0, fontSize:6.5, color:"rgba(255,255,255,0.5)", lineHeight:1.4,
                  fontFamily:"sans-serif" }}>{card.desc.length>50?card.desc.slice(0,50)+"…":card.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PvTestimonials({ d }) {
  const tag    = d.testiSectionTitle || "Words from Our Patients";
  const testi  = [
    { name:d.testi1Name||"Meera Sharma",   loc:d.testi1Loc||"Pune",      text:d.testi1Text||"After years of chronic fatigue, the Ayurvedic treatment truly transformed my life." },
    { name:d.testi2Name||"Rajesh Nair",    loc:d.testi2Loc||"Mumbai",    text:d.testi2Text||"The Pañcakarma therapy here is exceptional. Everything is done with care and authenticity." },
    { name:d.testi3Name||"Anita Kulkarni", loc:d.testi3Loc||"Nashik",    text:d.testi3Text||"The dietary guidance and herbal medicines worked wonders for my digestion." },
    { name:d.testi4Name||"Suresh Patel",   loc:d.testi4Loc||"Ahmedabad", text:d.testi4Text||"The Shirodhara session was the most calming experience of my life." },
  ].filter(t => t.name);
  return (
    <div style={{ background:"#fff", borderRadius:10, padding:"14px", textAlign:"center" }}>
      <p style={{ fontSize:7, fontWeight:700, letterSpacing:"0.12em", color:C.saffronD,
        textTransform:"uppercase", fontFamily:"sans-serif", marginBottom:6 }}>Patient Stories</p>
      <p style={{ fontFamily:"Georgia,serif", fontSize:11, fontWeight:700,
        color:C.ink, marginBottom:10 }}>{tag}</p>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
        {testi.slice(0,4).map((t,i)=>(
          <div key={i} style={{ background:C.saffronL, borderRadius:8, padding:"8px",
            border:`1px solid ${C.saffronL}` }}>
            <div style={{ fontSize:14, color:C.saffron, opacity:0.3, lineHeight:0.8,
              fontFamily:"serif", marginBottom:2 }}>"</div>
            <p style={{ fontSize:7, color:C.inkMid, lineHeight:1.6, fontFamily:"Georgia,serif",
              fontStyle:"italic", margin:"0 0 5px" }}>
              {t.text.length>70 ? t.text.slice(0,70)+"…" : t.text}
            </p>
            <p style={{ fontSize:7, fontWeight:700, color:C.ink, margin:0 }}>{t.name}</p>
            <p style={{ fontSize:6.5, color:C.inkLight, margin:0 }}>{t.loc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── About Page Extended Preview ── */
function PvAboutSections({ d }) {
  const coreTag  = d.coreValuesTag     || "What We Stand For";
  const coreH    = d.coreValuesHeading || "Our Core Values";
  const whyTag   = d.whyChooseUsTag    || "Why Choose Us";
  const whyH     = d.whyChooseUsHeading|| "What Sets Us Apart";
  const ctaH     = d.aboutCtaHeading   || "Ready to Begin Your Healing?";
  const ctaSub   = d.aboutCtaSub       || "Book a consultation and experience authentic Ayurvedic care.";
  const ctaBtn   = d.aboutCtaBtn       || "Book Appointment";
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {/* Core Values preview */}
      <div style={{ background:C.cream, borderRadius:10, padding:"12px" }}>
        <span style={{ fontSize:7, fontWeight:700, letterSpacing:"0.12em", color:C.saffronD,
          textTransform:"uppercase", fontFamily:"sans-serif",
          background:C.saffronL, borderRadius:20, padding:"2px 8px" }}>{coreTag}</span>
        <p style={{ fontFamily:"Georgia,serif", fontSize:10, fontWeight:700,
          color:C.ink, margin:"4px 0 6px" }}>{coreH}</p>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:4 }}>
          {["value1","value2","value3","value4"].map((k,i)=>(<div key={i}
            style={{ background:"#fff", borderRadius:6, padding:"6px 5px", textAlign:"center" }}>
            <div style={{ fontSize:12, marginBottom:3 }}>{"🌿⚖️🫁📚"[i*2]}</div>
            <p style={{ fontSize:6.5, fontWeight:700, color:C.ink, fontFamily:"sans-serif" }}>
              {d[k] || ["Authenticity","Ethics","Patient Care","Learning"][i]}
            </p>
          </div>))}
        </div>
      </div>
      {/* Why Choose Us */}
      <div style={{ background:`linear-gradient(135deg,${C.sageD},${C.sage})`,
        borderRadius:10, padding:"12px" }}>
        <span style={{ fontSize:7, fontWeight:700, letterSpacing:"0.12em",
          color:"rgba(255,255,255,0.85)", textTransform:"uppercase", fontFamily:"sans-serif",
          background:"rgba(255,255,255,0.15)", borderRadius:20, padding:"2px 8px" }}>{whyTag}</span>
        <p style={{ fontFamily:"Georgia,serif", fontSize:10, fontWeight:700,
          color:"#fff", margin:"4px 0 6px" }}>{whyH}</p>
        {["whyChooseUs1","whyChooseUs2","whyChooseUs3","whyChooseUs4"].map((k,i)=>(
          <div key={i} style={{ display:"flex", alignItems:"center", gap:5, marginBottom:3 }}>
            <div style={{ width:12, height:12, borderRadius:"50%", flexShrink:0,
              background:`linear-gradient(135deg,${C.turmeric},${C.saffron})`,
              display:"flex", alignItems:"center", justifyContent:"center" }}>
              <svg width="6" height="6" viewBox="0 0 10 10">
                <path d="M1.5 5L4 7.5L8.5 2.5" stroke={C.ink} strokeWidth="2" strokeLinecap="round" fill="none"/>
              </svg>
            </div>
            <span style={{ fontSize:7, color:"rgba(255,255,255,0.88)", fontFamily:"sans-serif" }}>
              {d[k] || ["Classical diagnosis","Qualified BAMS","Evidence-based","Patient education"][i]}
            </span>
          </div>
        ))}
      </div>
      {/* CTA preview */}
      <div style={{ background:"#fff", borderRadius:10, padding:"12px", textAlign:"center" }}>
        <p style={{ fontFamily:"Georgia,serif", fontSize:10, fontWeight:700, color:C.ink, margin:"0 0 4px" }}>{ctaH}</p>
        <p style={{ fontSize:7.5, color:C.inkLight, fontFamily:"sans-serif", margin:"0 0 7px" }}>{ctaSub.slice(0,70)}</p>
        <span style={{ padding:"4px 12px", borderRadius:20, fontSize:8, fontWeight:700,
          background:`linear-gradient(135deg,${C.saffron},${C.terra})`, color:"#fff" }}>🌿 {ctaBtn}</span>
      </div>
    </div>
  );
}

function PvAboutValues({ d }) { return <PvAboutSections d={d}/>; }

/* ── Services Extended Preview ── */
function PvSvcStats({ d }) {
  const stats = [
    { n:d.statBand1N||"100%",  l:d.statBand1L||"Natural Ingredients" },
    { n:d.statBand2N||"5000+", l:d.statBand2L||"Years of Wisdom"      },
    { n:d.statBand3N||"25+",   l:d.statBand3L||"Therapies Available"  },
    { n:d.statBand4N||"Safe",  l:d.statBand4L||"Evidence-Based Care"  },
  ];
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      <div style={{ background:`linear-gradient(135deg,${C.sageD},${C.sage})`,
        borderRadius:10, padding:"12px",
        display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:6, textAlign:"center" }}>
        {stats.map((s,i)=>(<div key={i}>
          <div style={{ fontFamily:"Georgia,serif", fontSize:12, fontWeight:800,
            color:C.turmeric }}>{s.n}</div>
          <div style={{ fontSize:6.5, color:"rgba(255,255,255,0.7)", marginTop:2,
            fontFamily:"sans-serif" }}>{s.l}</div>
        </div>))}
      </div>
      <div style={{ background:"#fff", borderRadius:10, padding:"12px", textAlign:"center" }}>
        <p style={{ fontFamily:"Georgia,serif", fontSize:10, fontWeight:700,
          color:C.ink, margin:"0 0 4px" }}>{d.svcCtaHeading || "Begin Your Healing Journey Today"}</p>
        <p style={{ fontSize:7.5, color:C.inkLight, fontFamily:"sans-serif", margin:"0 0 7px" }}>
          {(d.svcCtaSub||"Schedule a consultation and take the first step towards holistic wellness.").slice(0,70)}
        </p>
        <div style={{ display:"flex", gap:5, justifyContent:"center" }}>
          <span style={{ padding:"4px 10px", borderRadius:20, fontSize:8, fontWeight:700,
            background:`linear-gradient(135deg,${C.saffron},${C.terra})`, color:"#fff" }}>
            🌿 {d.svcCtaBtn||"Book Appointment"}
          </span>
          <span style={{ padding:"4px 10px", borderRadius:20, fontSize:8, fontWeight:600,
            border:`1.5px solid ${C.saffron}`, color:C.saffron }}>
            {d.svcCtaBtn2||"Learn About Us"}
          </span>
        </div>
      </div>
    </div>
  );
}

function PvSvcCards({ d }) {
  const cards = [1,2,3,4,5,6].map(n=>({
    title: d[`service${n}`]     || ["Ayurvedic Consultation","Pañcakarma","Diet & Lifestyle","Shirodhara","Yoga & Pranayama","Herbal Medicines"][n-1],
    desc:  d[`service${n}Desc`] || "Personalized, classical treatment aligned with your prakṛti.",
  }));
  const colBgs=[C.sageL,C.saffronL,"#FFF5F0",C.sageL,"#F0FFF4","#F0F4FF"];
  const icons=["🌿","🧘","🌸","🍵","✨","🌺"];
  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:5 }}>
      {cards.map((c,i)=>(
        <div key={i} style={{ background:"#fff", borderRadius:8, padding:"8px 7px",
          border:`1px solid ${colBgs[i]}30`, boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
          <div style={{ width:20, height:20, borderRadius:5, background:colBgs[i],
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:10, marginBottom:4 }}>{icons[i]}</div>
          <p style={{ fontFamily:"Georgia,serif", fontSize:7.5, fontWeight:700,
            color:C.ink, marginBottom:2 }}>{c.title}</p>
          <p style={{ fontSize:6.5, color:C.inkLight, lineHeight:1.4, fontFamily:"sans-serif" }}>
            {c.desc.slice(0,50)}{c.desc.length>50?"…":""}
          </p>
        </div>
      ))}
    </div>
  );
}

/* ── Our Doctors Page Preview ── */
function PvOurDoctors({ d }) {
  return (
    <div style={{ background:C.cream, borderRadius:10, overflow:"hidden" }}>
      <div style={{ padding:"16px", textAlign:"center",
        background:`linear-gradient(145deg,${C.cream},${C.creamD},${C.saffronL})` }}>
        <span style={{ fontSize:7, fontWeight:700, letterSpacing:"0.12em", color:C.saffronD,
          textTransform:"uppercase", fontFamily:"sans-serif",
          background:C.saffronL, borderRadius:20, padding:"2px 8px" }}>
          {d.odSectionTag || "Meet Our Physicians"}
        </span>
        <h1 style={{ fontFamily:"Georgia,serif", fontSize:13, fontWeight:800,
          color:C.ink, margin:"5px 0 4px" }}>{d.odTitle || "Our Doctors"}</h1>
        <p style={{ fontSize:7.5, color:C.inkMid, fontFamily:"Georgia,serif",
          lineHeight:1.6, maxWidth:270, margin:"0 auto 6px" }}>
          {(d.odSubtitle || "Experienced Ayurvedic physicians with classical training.").slice(0,90)}
        </p>
        <div style={{ display:"inline-flex", alignItems:"center", gap:5,
          background:"rgba(74,124,89,0.09)", borderRadius:20, padding:"4px 10px" }}>
          <span style={{ fontSize:10 }}>👆</span>
          <span style={{ fontSize:7, color:C.inkMid, fontFamily:"sans-serif" }}>
            {d.odFlipHint || "Tap any card to flip and read the doctor's health insight"}
          </span>
        </div>
      </div>
      {/* Sample doctor card placeholder */}
      <div style={{ padding:"10px" }}>
        <div style={{ background:"#fff", borderRadius:12, overflow:"hidden",
          boxShadow:"0 4px 14px rgba(0,0,0,0.08)" }}>
          <div style={{ height:50, background:`linear-gradient(160deg,${C.sageD},${C.sage},${C.saffron})`,
            display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ fontSize:22, opacity:0.6 }}>🩺</span>
          </div>
          <div style={{ padding:"8px", textAlign:"center" }}>
            <p style={{ fontSize:8, fontWeight:700, color:C.ink, margin:"0 0 2px",
              fontFamily:"Georgia,serif" }}>Dr. Example · BAMS</p>
            <p style={{ fontSize:7, color:C.inkLight, margin:0 }}>Sample profile card</p>
          </div>
        </div>
      </div>
      {/* CTA */}
      <div style={{ background:"#fff", padding:"10px", textAlign:"center" }}>
        <p style={{ fontFamily:"Georgia,serif", fontSize:9, fontWeight:700,
          color:C.ink, margin:"0 0 3px" }}>{d.odCtaHeading || "Ready to Begin Your Healing?"}</p>
        <p style={{ fontSize:7, color:C.inkLight, fontFamily:"sans-serif", margin:"0 0 6px" }}>
          {(d.odCtaSub || "Book a consultation with our expert physicians today.").slice(0,60)}
        </p>
        <span style={{ padding:"4px 12px", borderRadius:20, fontSize:8, fontWeight:700,
          background:`linear-gradient(135deg,${C.saffron},${C.terra})`, color:"#fff" }}>
          🌿 {d.odCtaBtn || "Book Appointment"}
        </span>
      </div>
    </div>
  );
}

/* ── Seasonal Regimen Preview ── */
function PvSeasonalRegimen({ d }) {
  const heading1   = d.srHeading1   || "Seasonal Regimen";
  const subtitle   = d.srSubtitle   || "Simple Ayurvedic guidance for the general public — live in harmony with nature's rhythms.";
  const badge      = d.srBadge      || "Seasonal Wisdom";
  const imgUrl     = d.seasonImageUrl || "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=70";
  const sEmoji     = d.seasonEmoji   || "🌿";
  const sLabel     = d.seasonLabel   || "Current Season";
  const sName      = d.seasonName    || "Vasanta Ṛtu";
  const sPeriod    = d.seasonPeriod  || "Spring · Mar – May";
  const vaidyaTip  = d.srVaidyaTip   || "Start your day with warm honey-ginger water and a 15-minute walk outdoors.";
  const dos   = (d.srDos    || "Eat light warm meals|Wake at sunrise|Use bitter herbs|Light yoga daily|Drink ginger water").split("|").map(s=>s.trim()).filter(Boolean);
  const avoids= (d.srAvoids || "Heavy oily foods|Day sleep|Cold drinks|Excess sour intake|Skipping meals").split("|").map(s=>s.trim()).filter(Boolean);
  const miniCards = [
    { icon:d.srCard1Icon||"🌅", title:d.srCard1Title||"Best Wake Time",  desc:d.srCard1Desc||"5:30 – 6:00 AM"   },
    { icon:d.srCard2Icon||"🌾", title:d.srCard2Title||"Ideal Foods",     desc:d.srCard2Desc||"Barley, mung, honey"},
    { icon:d.srCard3Icon||"🧘", title:d.srCard3Title||"Mind Practice",   desc:d.srCard3Desc||"Meditation 10 min" },
    { icon:d.srCard4Icon||"🌿", title:d.srCard4Title||"Key Herb",        desc:d.srCard4Desc||"Triphala at bedtime"},
  ];
  return (
    <div style={{ background:"linear-gradient(135deg,#1a1a2e 0%,#16213e 50%,#0f3460 100%)",
      borderRadius:10, overflow:"hidden", position:"relative" }}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr" }}>
        {/* Photo side */}
        <div style={{ position:"relative", height:200, overflow:"hidden" }}>
          <img src={imgUrl} alt="Season"
            style={{ width:"100%", height:"100%", objectFit:"cover",
              filter:"brightness(0.65) saturate(1.1)", display:"block" }}/>
          <div style={{ position:"absolute", inset:0,
            background:"linear-gradient(to right,rgba(10,8,20,0.5),transparent)",
            pointerEvents:"none" }}/>
          <div style={{ position:"absolute", inset:0,
            background:"linear-gradient(to top,rgba(15,52,96,0.65),transparent 55%)",
            pointerEvents:"none" }}/>
          {/* Season badge */}
          <div style={{ position:"absolute", top:8, left:8,
            display:"inline-flex", alignItems:"center", gap:4,
            background:"rgba(247,201,72,0.2)", backdropFilter:"blur(8px)",
            borderRadius:12, padding:"3px 8px",
            border:"1px solid rgba(247,201,72,0.4)" }}>
            <span style={{ fontSize:9 }}>{sEmoji}</span>
            <span style={{ fontSize:6.5, fontWeight:700, color:C.turmeric,
              textTransform:"uppercase", letterSpacing:"0.1em", fontFamily:"sans-serif" }}>
              {sLabel}
            </span>
          </div>
          {/* Season name bottom left */}
          <div style={{ position:"absolute", bottom:8, left:8 }}>
            <p style={{ margin:0, fontSize:10, fontFamily:"Georgia,serif",
              fontWeight:800, color:"#fff", textShadow:"0 1px 6px rgba(0,0,0,0.5)" }}>{sName}</p>
            <p style={{ margin:"2px 0 0", fontSize:7, color:"rgba(255,255,255,0.65)",
              fontFamily:"Georgia,serif", fontStyle:"italic" }}>{sPeriod}</p>
          </div>
        </div>

        {/* Content side */}
        <div style={{ padding:"10px 11px", display:"flex", flexDirection:"column", gap:6 }}>
          {/* Badge */}
          <div style={{ display:"inline-flex", alignItems:"center", gap:4,
            background:"rgba(255,255,255,0.08)", borderRadius:10, padding:"2px 8px",
            border:"1px solid rgba(255,255,255,0.15)", alignSelf:"flex-start" }}>
            <span style={{ fontSize:8 }}>🍃</span>
            <span style={{ fontSize:6, fontWeight:700, letterSpacing:"0.1em",
              color:"rgba(255,255,255,0.7)", textTransform:"uppercase", fontFamily:"sans-serif" }}>
              {badge}
            </span>
          </div>

          {/* Headline */}
          <h2 style={{ fontFamily:"Georgia,serif", fontSize:10, fontWeight:800,
            color:"#fff", lineHeight:1.2, margin:0 }}>{heading1}</h2>
          <p style={{ fontSize:7, color:"rgba(255,255,255,0.5)", fontFamily:"Georgia,serif",
            fontStyle:"italic", margin:0, lineHeight:1.5 }}>
            {subtitle.length>70 ? subtitle.slice(0,70)+"…" : subtitle}
          </p>

          {/* Do's & Avoid two cols */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:5 }}>
            <div style={{ background:"rgba(74,124,89,0.2)", borderRadius:6, padding:"6px 7px",
              border:"1px solid rgba(74,124,89,0.3)" }}>
              <div style={{ display:"flex", alignItems:"center", gap:3, marginBottom:4 }}>
                <span style={{ fontSize:8 }}>✅</span>
                <span style={{ fontSize:6, fontWeight:800, color:"rgba(144,238,144,0.9)",
                  textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:"sans-serif" }}>Do's</span>
              </div>
              {dos.slice(0,4).map((item,i)=>(
                <div key={i} style={{ display:"flex", gap:4, alignItems:"flex-start", marginBottom:2 }}>
                  <div style={{ width:4, height:4, borderRadius:"50%", flexShrink:0,
                    background:"rgba(144,238,144,0.65)", marginTop:3 }}/>
                  <span style={{ fontSize:6.5, color:"rgba(255,255,255,0.75)", fontFamily:"sans-serif", lineHeight:1.4 }}>{item}</span>
                </div>
              ))}
            </div>
            <div style={{ background:"rgba(184,92,56,0.15)", borderRadius:6, padding:"6px 7px",
              border:"1px solid rgba(184,92,56,0.28)" }}>
              <div style={{ display:"flex", alignItems:"center", gap:3, marginBottom:4 }}>
                <span style={{ fontSize:8 }}>🚫</span>
                <span style={{ fontSize:6, fontWeight:800, color:"rgba(255,160,122,0.9)",
                  textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:"sans-serif" }}>Avoid</span>
              </div>
              {avoids.slice(0,4).map((item,i)=>(
                <div key={i} style={{ display:"flex", gap:4, alignItems:"flex-start", marginBottom:2 }}>
                  <div style={{ width:4, height:4, borderRadius:"50%", flexShrink:0,
                    background:"rgba(255,160,122,0.6)", marginTop:3 }}/>
                  <span style={{ fontSize:6.5, color:"rgba(255,255,255,0.75)", fontFamily:"sans-serif", lineHeight:1.4 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Vaidya tip */}
          <div style={{ background:"rgba(247,201,72,0.1)", borderRadius:6, padding:"6px 8px",
            border:"1px solid rgba(247,201,72,0.22)", display:"flex", gap:5 }}>
            <span style={{ fontSize:10, flexShrink:0 }}>💡</span>
            <div>
              <p style={{ margin:"0 0 1px", fontSize:6, fontWeight:700, color:C.turmeric,
                textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:"sans-serif" }}>Vaidya's Tip</p>
              <p style={{ margin:0, fontSize:6.5, color:"rgba(255,255,255,0.68)",
                fontFamily:"Georgia,serif", fontStyle:"italic", lineHeight:1.5 }}>
                {vaidyaTip.length>80 ? vaidyaTip.slice(0,80)+"…" : vaidyaTip}
              </p>
            </div>
          </div>

          {/* Mini cards */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:4 }}>
            {miniCards.map((card,i)=>(
              <div key={i} style={{ background:"rgba(255,255,255,0.06)",
                border:"1px solid rgba(255,255,255,0.09)", borderRadius:6,
                padding:"5px 6px", display:"flex", gap:5, alignItems:"center" }}>
                <span style={{ fontSize:11, flexShrink:0 }}>{card.icon}</span>
                <div>
                  <p style={{ margin:0, fontSize:5.5, fontWeight:700,
                    color:"rgba(255,255,255,0.45)", fontFamily:"sans-serif",
                    textTransform:"uppercase", letterSpacing:"0.07em" }}>{card.title}</p>
                  <p style={{ margin:0, fontSize:7, color:"rgba(255,255,255,0.85)",
                    fontFamily:"sans-serif", fontWeight:600 }}>{card.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Group id → preview component ── */
const GROUP_PREVIEW = {
  "hero":             PvHero,
  "about":            PvAbout,
  "stats":            PvStats,
  "treatments":       PvTreatments,
  "trust":            PvTrust,
  "online-consult":   PvOnlineConsult,
  "testimonials":     PvTestimonials,
  "about-sections":   PvAboutSections,
  "about-values":     PvAboutValues,
  "svc-stats":        PvSvcStats,
  "svc-cta":          PvSvcStats,
  "svc-cards":        PvSvcCards,
  "svc-header":       PvServicesPage,
  "od-hero":          PvOurDoctors,
  "od-card-labels":   PvOurDoctors,
  "od-cta":           PvOurDoctors,
  "about-hero":       PvAboutPage,
  "about-cards":      PvAboutPage,
  "about-meta":       PvAboutPage,
  "contact-heading":  PvContactPage,
  "contact-fields":   PvContactPage,
};

const PAGE_FULL = {
  home:    [PvHero, PvAbout, PvStats, PvTreatments, PvTrust, PvOnlineConsult, PvTestimonials],
  "our-doctors": [PvOurDoctors],
  about:   [PvAboutPage],
  services:[PvServicesPage],
  contact: [PvContactPage],
};

/* ═══════════════════════════════════════════════════════════
   PREVIEW PANEL
═══════════════════════════════════════════════════════════ */
function PreviewPanel({ activePage, activeGroup, data }) {
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [activeGroup]);

  const GroupComp = activeGroup ? GROUP_PREVIEW[activeGroup] : null;
  const fullComps = PAGE_FULL[activePage] || [];
  const groupTitle = activeGroup
    ? PAGES[activePage]?.groups.find(g=>g.id===activeGroup)?.title
    : null;

  return (
    <div style={{
      position:"sticky", top:16, height:"calc(100vh - 140px)",
      display:"flex", flexDirection:"column",
      background:"#1a1a2e", borderRadius:18,
      boxShadow:"0 20px 60px rgba(0,0,0,0.28)", overflow:"hidden",
    }}>
      {/* Browser chrome */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"9px 14px", background:"#0f0f1a", flexShrink:0 }}>
        <div style={{ display:"flex", gap:5 }}>
          {["#ff5f57","#febc2e","#28c840"].map(c=>(
            <div key={c} style={{ width:9, height:9, borderRadius:"50%", background:c }}/>
          ))}
        </div>
        <div style={{ flex:1, margin:"0 10px", background:"rgba(255,255,255,0.07)",
          borderRadius:5, padding:"3px 10px", display:"flex", alignItems:"center", gap:5 }}>
          <div style={{ width:5, height:5, borderRadius:"50%", background:"#22c55e", flexShrink:0 }}/>
          <span style={{ fontSize:9, color:"rgba(255,255,255,0.38)", fontFamily:"monospace",
            overflow:"hidden", whiteSpace:"nowrap" }}>
            yourclinic.com/{activePage==="home"?"":activePage}
          </span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:4,
          background:"rgba(34,197,94,0.15)", border:"1px solid rgba(34,197,94,0.3)",
          borderRadius:20, padding:"2px 8px" }}>
          <div style={{ width:5, height:5, borderRadius:"50%", background:"#22c55e",
            boxShadow:"0 0 5px #22c55e" }}/>
          <span style={{ fontSize:8, color:"#22c55e", fontWeight:700, fontFamily:"sans-serif" }}>LIVE</span>
        </div>
      </div>

      {/* Focused section label */}
      <AnimatePresence>
        {groupTitle && (
          <motion.div initial={{ opacity:0, y:-6 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-6 }}
            style={{ padding:"5px 14px", background:"rgba(232,146,42,0.12)",
              borderBottom:"1px solid rgba(232,146,42,0.18)",
              display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
            <span style={{ fontSize:8.5, color:C.saffron, fontWeight:700,
              fontFamily:"sans-serif", textTransform:"uppercase", letterSpacing:"0.1em" }}>
              Previewing:
            </span>
            <span style={{ fontSize:8.5, color:"rgba(255,255,255,0.65)", fontFamily:"sans-serif" }}>
              {groupTitle}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      <div ref={scrollRef} style={{ flex:1, overflowY:"auto", padding:"10px",
        background:"#f5f5f4", scrollbarWidth:"thin",
        scrollbarColor:"rgba(0,0,0,0.15) transparent" }}>
        <AnimatePresence mode="wait">
          <motion.div key={`${activePage}-${activeGroup}`}
            initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }}
            exit={{ opacity:0, y:-6 }} transition={{ duration:0.15 }}>
            {GroupComp ? (
              <GroupComp d={data}/>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
                {fullComps.map((Comp,i)=><Comp key={i} d={data}/>)}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div style={{ padding:"6px 14px", background:"#0f0f1a", flexShrink:0, textAlign:"center" }}>
        <p style={{ fontSize:8, color:"rgba(255,255,255,0.28)", fontFamily:"sans-serif" }}>
          {activeGroup ? "Edits reflect instantly  ✦  Save to push live" : "Click any section header to focus its preview"}
        </p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function AdminSite() {
  const [activePage,  setActivePage]  = useState("home");
  const [activeGroup, setActiveGroup] = useState(null);
  const [data,        setData]        = useState({});
  const [savedData,   setSavedData]   = useState({}); // last-saved snapshot for discard
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [dirty,       setDirty]       = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [error,       setError]       = useState("");
  const [layout,      setLayout]      = useState("split");
  const [isSEO,       setIsSEO]       = useState(false);
  const [seoData,     setSeoData]     = useState({});
  const [seoSavedData,setSeoSavedData]= useState({});
  const [seoDirty,    setSeoDirty]    = useState(false);
  const [seoSaving,   setSeoSaving]   = useState(false);
  const [seoSaved,    setSeoSaved]    = useState(false);
  const [seoError,    setSeoError]    = useState("");

  // ── Clinic Mode ──
  const [clinicMode,     setClinicMode]     = useState("online_only"); // "physical" | "online_only"
  const [modeSaving,     setModeSaving]     = useState(false);
  const [modeSaved,      setModeSaved]      = useState(false);

  useEffect(() => {
    api.get("/settings/clinicMode")
      .then(r => { if (r.data === "physical" || r.data === "online_only") setClinicMode(r.data); })
      .catch(() => {});
  }, []);

  const saveClinicMode = async (newMode) => {
    setClinicMode(newMode);
    setModeSaving(true); setModeSaved(false);
    try {
      await api.post("/settings", { key: "clinicMode", value: newMode });
      setModeSaved(true);
      setTimeout(() => setModeSaved(false), 2500);
    } catch { /* silently fail */ }
    finally { setModeSaving(false); }
  };

  const showEditor  = layout !== "preview";
  const showPreview = layout !== "editor";

  const pageConf    = PAGES[activePage];
  const totalFields = pageConf.groups.reduce((a,g) => a + g.fields.length, 0);

  const load = async () => {
    setLoading(true); setDirty(false); setError(""); setActiveGroup(null);
    try {
      const res = await api.get(`/content/${activePage}`);
      const d = res.data?.data || {};
      setData(d);
      setSavedData(d);
    } catch { setError("Failed to load content."); }
    finally   { setLoading(false); }
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      await api.put(`/content/${activePage}`, { data });
      setSavedData(data);
      setDirty(false); setSaved(true);
      setTimeout(() => setSaved(false), 2800);
    } catch { setError("Failed to save. Check your connection."); }
    finally   { setSaving(false); }
  };

  const discard = () => {
    setData(savedData);
    setDirty(false);
    setSaved(false);
  };

  const loadSEO = async () => {
    try {
      const res = await api.get("/content/seo");
      const d = res.data?.data || {};
      setSeoData(d);
      setSeoSavedData(d);
    } catch { setSeoError("Failed to load SEO settings."); }
  };

  const saveSEO = async () => {
    setSeoSaving(true); setSeoError("");
    try {
      await api.put("/content/seo", { data: seoData });
      setSeoSavedData(seoData);
      setSeoDirty(false); setSeoSaved(true);
      setTimeout(() => setSeoSaved(false), 2800);
    } catch { setSeoError("Failed to save SEO settings."); }
    finally { setSeoSaving(false); }
  };

  const updateSEO = (key, val) => {
    setSeoDirty(true); setSeoSaved(false);
    setSeoData(p => ({ ...p, [key]: val }));
  };

  useEffect(() => { load(); }, [activePage]); // eslint-disable-line
  useEffect(() => { loadSEO(); }, []); // eslint-disable-line

  const update = (key, val) => {
    setDirty(true); setSaved(false);
    setData(p => ({ ...p, [key]: val }));
  };

  return (
    <div className="space-y-4" style={{ maxWidth:"100%" }}>

      {/* Header */}
      <div style={{ display:"flex", alignItems:"flex-start",
        justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">🌐 Website Content</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Every visible word on your site is editable here — including labels, badges and Sanskrit text.
            <span className="text-gray-400 ml-1">({totalFields} fields)</span>
          </p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:2,
          background:"#f1f5f9", borderRadius:12, padding:3 }}>
          {[
            { k:"editor",  icon:"✏️", label:"Editor"  },
            { k:"split",   icon:"◫",  label:"Split"   },
            { k:"preview", icon:"👁️", label:"Preview" },
          ].map(m=>(
            <button key={m.k} onClick={()=>setLayout(m.k)}
              style={{ padding:"6px 13px", borderRadius:9, fontSize:12, fontWeight:600,
                cursor:"pointer", border:"none", transition:"all 0.15s",
                background: layout===m.k ? "#fff" : "transparent",
                color:       layout===m.k ? "#1f2937" : "#94a3b8",
                boxShadow:   layout===m.k ? "0 1px 4px rgba(0,0,0,0.10)" : "none" }}>
              {m.icon} {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Clinic Mode Toggle ── */}
      <div style={{
        background: clinicMode === "physical"
          ? "linear-gradient(135deg,#fdf0d5 0%,#fde8c8 100%)"
          : "linear-gradient(135deg,#eaf4ee 0%,#d1f0dc 100%)",
        border: `2px solid ${clinicMode === "physical" ? C.saffron : C.sage}40`,
        borderRadius: 16, padding: "16px 20px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 16, flexWrap: "wrap",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{
            width:48, height:48, borderRadius:14, flexShrink:0,
            background: clinicMode === "physical"
              ? `linear-gradient(135deg,${C.saffron},${C.terra})`
              : `linear-gradient(135deg,${C.sage},${C.sageD})`,
            display:"flex", alignItems:"center", justifyContent:"center", fontSize:22,
            boxShadow:`0 4px 12px rgba(0,0,0,0.12)`,
          }}>
            {clinicMode === "physical" ? "🏥" : "💻"}
          </div>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:2 }}>
              <span style={{ fontSize:15, fontWeight:800, color:C.ink, fontFamily:"'Playfair Display',serif" }}>
                Clinic Mode
              </span>
              {modeSaved && (
                <span style={{ fontSize:11, color:"#16a34a", fontWeight:600,
                  background:"#dcfce7", borderRadius:20, padding:"2px 8px" }}>
                  ✓ Saved & live
                </span>
              )}
              {modeSaving && (
                <span style={{ fontSize:11, color:C.inkLight }}>Saving…</span>
              )}
            </div>
            <p style={{ fontSize:12, color:C.inkMid, margin:0, fontFamily:"sans-serif" }}>
              {clinicMode === "physical"
                ? "Physical clinic mode — address, map & clinic hours shown on homepage"
                : "Online-only mode — contact strip shows email/phone/WhatsApp only (no address shown)"}
            </p>
          </div>
        </div>

        {/* Toggle switch */}
        <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
          <span style={{ fontSize:13, fontWeight:600,
            color: clinicMode === "online_only" ? C.sageD : "#94a3b8",
            fontFamily:"sans-serif" }}>
            Online Only
          </span>
          <div
            onClick={() => saveClinicMode(clinicMode === "physical" ? "online_only" : "physical")}
            style={{
              width:56, height:28, borderRadius:14, cursor:"pointer",
              background: clinicMode === "physical"
                ? `linear-gradient(135deg,${C.saffron},${C.terra})`
                : "#e5e7eb",
              position:"relative", transition:"background 0.3s",
              boxShadow:"inset 0 1px 4px rgba(0,0,0,0.12)",
            }}
          >
            <div style={{
              width:22, height:22, borderRadius:11,
              background:"#fff",
              position:"absolute", top:3,
              left: clinicMode === "physical" ? 31 : 3,
              transition:"left 0.28s cubic-bezier(0.4,0,0.2,1)",
              boxShadow:"0 2px 6px rgba(0,0,0,0.2)",
            }}/>
          </div>
          <span style={{ fontSize:13, fontWeight:600,
            color: clinicMode === "physical" ? C.saffronD : "#94a3b8",
            fontFamily:"sans-serif" }}>
            Physical Clinic
          </span>
        </div>
      </div>

      {/* Page tabs */}
      <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
        {Object.entries(PAGES).map(([key,cfg])=>(
          <button key={key} onClick={()=>{ setActivePage(key); setIsSEO(false); }}
            style={{ padding:"7px 16px", borderRadius:20, fontSize:13, fontWeight:600,
              cursor:"pointer", border:"none", transition:"all 0.2s",
              background: !isSEO && activePage===key
                ? `linear-gradient(135deg,${C.sage},${C.sageD})`
                : "#fff",
              color:       !isSEO && activePage===key ? "#fff" : "#4b5563",
              boxShadow:   !isSEO && activePage===key
                ? `0 4px 14px rgba(74,124,89,0.35)`
                : "0 1px 3px rgba(0,0,0,0.08)",
              transform:   !isSEO && activePage===key ? "translateY(-1px)" : "none" }}>
            {cfg.label}
          </button>
        ))}
        {/* SEO Tab */}
        <button onClick={()=>setIsSEO(true)}
          style={{ padding:"7px 16px", borderRadius:20, fontSize:13, fontWeight:600,
            cursor:"pointer", border:"none", transition:"all 0.2s",
            background: isSEO
              ? `linear-gradient(135deg,${C.terra},${C.saffronD})`
              : "#fff",
            color: isSEO ? "#fff" : "#4b5563",
            boxShadow: isSEO
              ? `0 4px 14px rgba(184,92,56,0.35)`
              : "0 1px 3px rgba(0,0,0,0.08)",
            transform: isSEO ? "translateY(-1px)" : "none" }}>
          🔍 SEO Settings
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-2.5 text-sm">
          ⚠ {error}
        </div>
      )}

      {/* ── SEO PANEL — shown when isSEO tab active ── */}
      {isSEO && (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {/* Info banner */}
          <div style={{ background:`linear-gradient(90deg,${C.saffronL},#fff)`,
            border:`1px solid ${C.saffron}40`, borderRadius:14, padding:"14px 18px",
            display:"flex", gap:12, alignItems:"flex-start" }}>
            <span style={{ fontSize:22 }}>🔍</span>
            <div>
              <div style={{ fontSize:14, fontWeight:700, color:C.ink, marginBottom:4 }}>
                SEO Settings
              </div>
              <div style={{ fontSize:12, color:C.inkMid, lineHeight:1.7 }}>
                Edit any field below and click <b>Save SEO</b>. Changes go live on the next page visit — no code push needed.
                The <code style={{ fontSize:11, background:"#f3f4f6", padding:"1px 5px", borderRadius:4 }}>useSEO</code> hook
                reads these values from the database on every page load.
              </div>
            </div>
          </div>

          {seoError && (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-2.5 text-sm">
              ⚠ {seoError}
            </div>
          )}

          {SEO_FIELDS.map((group, gi) => (
            <motion.div key={group.id}
              initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
              transition={{ delay:gi*0.05 }}
              style={{ background:"#fff", borderRadius:14, overflow:"hidden",
                boxShadow:"0 1px 4px rgba(0,0,0,0.07)" }}>
              <div style={{ padding:"11px 16px", borderBottom:"1px solid #f3f4f6",
                background:"linear-gradient(90deg,#fafafa,#fff)" }}>
                <h3 style={{ fontSize:13, fontWeight:700, color:"#1f2937", margin:0 }}>{group.title}</h3>
                {group.hint && <p style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>{group.hint}</p>}
              </div>
              <div style={{ padding:"13px 16px", display:"flex", flexDirection:"column", gap:10 }}>
                {group.fields.map(f => (
                  <div key={f.key}>
                    <label style={{ display:"block", fontSize:12, fontWeight:600,
                      color:"#374151", marginBottom:3 }}>{f.label}</label>
                    {f.hint && <p style={{ fontSize:10, color:"#9ca3af", marginBottom:4 }}>{f.hint}</p>}
                    {f.type==="textarea" ? (
                      <textarea
                        style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:9,
                          padding:"8px 12px", fontSize:13, minHeight:64, resize:"vertical",
                          outline:"none", fontFamily:"inherit", lineHeight:1.6, boxSizing:"border-box" }}
                        value={seoData[f.key]||""}
                        onChange={e=>updateSEO(f.key,e.target.value)}
                        placeholder={`Enter ${f.label.toLowerCase()}…`}
                        onFocus={e=>{e.target.style.border=`1.5px solid ${C.saffron}`;e.target.style.boxShadow=`0 0 0 3px ${C.saffronL}`;}}
                        onBlur={e=>{e.target.style.border="1.5px solid #e5e7eb";e.target.style.boxShadow="none";}}
                      />
                    ) : (
                      <input
                        style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:9,
                          padding:"8px 12px", fontSize:13, outline:"none",
                          boxSizing:"border-box", fontFamily:"inherit" }}
                        value={seoData[f.key]||""}
                        onChange={e=>updateSEO(f.key,e.target.value)}
                        placeholder={`Enter ${f.label.toLowerCase()}…`}
                        onFocus={e=>{e.target.style.border=`1.5px solid ${C.saffron}`;e.target.style.boxShadow=`0 0 0 3px ${C.saffronL}`;}}
                        onBlur={e=>{e.target.style.border="1.5px solid #e5e7eb";e.target.style.boxShadow="none";}}
                      />
                    )}
                    {/* Live char count for title/description */}
                    {(f.key==="seoTitle"||f.key==="seoDescription") && (
                      <p style={{ fontSize:10, marginTop:3,
                        color: (seoData[f.key]||"").length === 0 ? "#9ca3af"
                          : f.key==="seoTitle"
                            ? (seoData[f.key]||"").length <= 60 ? "#16a34a" : "#dc2626"
                            : (seoData[f.key]||"").length <= 155 ? "#16a34a" : "#dc2626"
                      }}>
                        {(seoData[f.key]||"").length} characters
                        {f.key==="seoTitle" ? " (ideal: 50–60)" : " (ideal: 120–155)"}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}

          {/* SEO Save bar */}
          <div style={{ position:"sticky", bottom:12, zIndex:20 }}>
            <div style={{ background:"rgba(255,255,255,0.95)", backdropFilter:"blur(12px)",
              border:"1px solid #e5e7eb", borderRadius:14,
              boxShadow:"0 8px 32px rgba(0,0,0,0.12)",
              padding:"12px 18px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontSize:13, color: seoDirty ? C.terra : "#9ca3af" }}>
                {seoSaved ? "✅ SEO settings saved!" : seoDirty ? "Unsaved SEO changes" : "All SEO changes saved"}
              </span>
              <button onClick={saveSEO} disabled={!seoDirty||seoSaving}
                style={{ padding:"9px 22px", borderRadius:10, border:"none",
                  background: seoDirty
                    ? `linear-gradient(135deg,${C.terra},${C.saffronD})`
                    : "#e5e7eb",
                  color: seoDirty ? "#fff" : "#9ca3af",
                  fontWeight:700, fontSize:13, cursor: seoDirty ? "pointer" : "default" }}>
                {seoSaving ? "Saving…" : "Save SEO"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Main grid: editor left, sticky preview right ── */}
      {!isSEO && <div style={{
        display:"grid",
        gridTemplateColumns:
          layout==="editor"  ? "1fr" :
          layout==="preview" ? "1fr" : "1fr 1fr",
        gap:16, alignItems:"start",
      }}>

        {/* Editor column */}
        {showEditor && (
          <div style={{ minWidth:0, display:"flex", flexDirection:"column", gap:10 }}>
            {loading ? (
              <div style={{ textAlign:"center", padding:"64px 0", color:"#9ca3af",
                fontSize:14, background:"#fff", borderRadius:16 }}>Loading content…</div>
            ) : pageConf.groups.map((group, gi) => {
              const isActive = activeGroup === group.id;
              return (
                <motion.div key={group.id}
                  initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
                  transition={{ delay:gi*0.04 }}
                  style={{
                    background:"#fff", borderRadius:14, overflow:"hidden",
                    boxShadow: isActive
                      ? `0 0 0 2px ${C.saffron}, 0 8px 24px rgba(232,146,42,0.12)`
                      : "0 1px 4px rgba(0,0,0,0.07)",
                    transition:"box-shadow 0.2s",
                  }}>

                  {/* Group header — click to focus preview */}
                  <div onClick={()=>setActiveGroup(isActive ? null : group.id)}
                    style={{
                      padding:"11px 16px", cursor:"pointer",
                      borderBottom:"1px solid #f3f4f6",
                      display:"flex", alignItems:"center", justifyContent:"space-between",
                      background: isActive
                        ? `linear-gradient(90deg,${C.saffronL},#fff)`
                        : "linear-gradient(90deg,#fafafa,#fff)",
                      transition:"background 0.2s",
                    }}>
                    <div>
                      <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                        {isActive && (
                          <div style={{ width:7, height:7, borderRadius:"50%",
                            background:C.saffron, flexShrink:0,
                            boxShadow:`0 0 6px ${C.saffron}` }}/>
                        )}
                        <h3 style={{ fontSize:13, fontWeight:700, color:"#1f2937", margin:0 }}>
                          {group.title}
                        </h3>
                        <span style={{ fontSize:10, color:"#9ca3af" }}>
                          ({group.fields.length} fields)
                        </span>
                      </div>
                      {group.hint && (
                        <p style={{ fontSize:11, color:"#9ca3af", marginTop:2,
                          marginLeft:isActive?14:0 }}>{group.hint}</p>
                      )}
                    </div>
                    {showPreview && (
                      <span style={{ fontSize:10, fontWeight:600, padding:"3px 10px",
                        borderRadius:20, flexShrink:0, transition:"all 0.2s",
                        background: isActive ? C.saffronL : "#f1f5f9",
                        color:      isActive ? C.saffronD : "#94a3b8" }}>
                        {isActive ? "👁 Previewing" : "Click to preview"}
                      </span>
                    )}
                  </div>

                  {/* Fields */}
                  <div style={{ padding:"13px 16px", display:"flex",
                    flexDirection:"column", gap:10 }}>
                    {group.fields.map(f=>(
                      <div key={f.key}>
                        <label style={{ display:"block", fontSize:12, fontWeight:600,
                          color:"#374151", marginBottom:3 }}>{f.label}</label>
                        {f.hint && (
                          <p style={{ fontSize:10, color:"#9ca3af", marginBottom:4 }}>{f.hint}</p>
                        )}
                        {f.type==="textarea" ? (
                          <textarea
                            style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:9,
                              padding:"8px 12px", fontSize:13, minHeight:64, resize:"vertical",
                              outline:"none", fontFamily:"inherit", lineHeight:1.6,
                              boxSizing:"border-box", transition:"border 0.15s, box-shadow 0.15s",
                              background: data[f.key] ? "#fff" : "#fafafa" }}
                            value={data[f.key]||""}
                            onChange={e=>update(f.key,e.target.value)}
                            placeholder={`Enter ${f.label.toLowerCase()}…`}
                            onFocus={e=>{e.target.style.border=`1.5px solid ${C.saffron}`;e.target.style.boxShadow=`0 0 0 3px ${C.saffronL}`;}}
                            onBlur={e=>{e.target.style.border="1.5px solid #e5e7eb";e.target.style.boxShadow="none";}}
                          />
                        ) : (
                          <input
                            style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:9,
                              padding:"8px 12px", fontSize:13, outline:"none",
                              boxSizing:"border-box", fontFamily:"inherit",
                              transition:"border 0.15s, box-shadow 0.15s",
                              background: data[f.key] ? "#fff" : "#fafafa" }}
                            value={data[f.key]||""}
                            onChange={e=>update(f.key,e.target.value)}
                            placeholder={`Enter ${f.label.toLowerCase()}…`}
                            onFocus={e=>{e.target.style.border=`1.5px solid ${C.saffron}`;e.target.style.boxShadow=`0 0 0 3px ${C.saffronL}`;}}
                            onBlur={e=>{e.target.style.border="1.5px solid #e5e7eb";e.target.style.boxShadow="none";}}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Preview column — sticky, scrolls independently */}
        {showPreview && (
          <div style={{ position:"sticky", top:16 }}>
            <PreviewPanel activePage={activePage} activeGroup={activeGroup} data={data}/>
          </div>
        )}
      </div>}

      {/* Sticky save bar — hidden when SEO tab active (SEO has its own save bar) */}
      {!isSEO && <div style={{ position:"sticky", bottom:12, zIndex:20 }} />}
      {isSEO ? null : null /* spacer */}

      {/* Sticky save bar */}
      <div style={{ position:"sticky", bottom:12, zIndex:20 }}>
        <div style={{
          background:"rgba(255,255,255,0.95)", backdropFilter:"blur(12px)",
          border:"1px solid #e5e7eb", borderRadius:14,
          boxShadow:"0 8px 32px rgba(0,0,0,0.12)",
          padding:"12px 18px", display:"flex", alignItems:"center", justifyContent:"space-between",
        }}>
          <AnimatePresence mode="wait">
            {dirty && !saved && (
              <motion.div key="u" initial={{opacity:0,x:-6}} animate={{opacity:1,x:0}} exit={{opacity:0}}
                style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:7,height:7,borderRadius:"50%",background:"#f97316"}}/>
                <span style={{fontSize:13,color:"#ea580c",fontWeight:600}}>
                  Unsaved changes — preview reflects edits live
                </span>
              </motion.div>
            )}
            {saved && (
              <motion.div key="s" initial={{opacity:0,x:-6}} animate={{opacity:1,x:0}} exit={{opacity:0}}
                style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:15}}>✅</span>
                <span style={{fontSize:13,color:"#16a34a",fontWeight:600}}>
                  Saved — changes are now live on the website
                </span>
              </motion.div>
            )}
            {!dirty && !saved && (
              <motion.p key="ok" initial={{opacity:0}} animate={{opacity:1}}
                style={{fontSize:13,color:"#9ca3af"}}>
                All changes saved · Click a section header to focus its preview
              </motion.p>
            )}
          </AnimatePresence>
          <div style={{ display:"flex", gap:10, alignItems:"center", flexShrink:0 }}>
            {dirty && (
              <button onClick={discard}
                style={{ fontSize:13, color:"#6b7280", padding:"7px 14px",
                  borderRadius:9, border:"1px solid #e5e7eb",
                  background:"transparent", cursor:"pointer", fontWeight:500 }}>
                ↩ Undo Changes
              </button>
            )}
            <button onClick={save} disabled={!dirty||saving}
              style={{ padding:"8px 22px", borderRadius:11, fontSize:13, fontWeight:700,
                color:"#fff", cursor:dirty&&!saving?"pointer":"not-allowed",
                border:"none", transition:"all 0.2s",
                background: dirty ? `linear-gradient(135deg,${C.sage},${C.sageD})` : "#d1d5db",
                boxShadow:  dirty ? `0 4px 14px rgba(74,124,89,0.38)` : "none",
                opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving…" : "💾 Save & Go Live"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}